// src/components/MspOsMobile.jsx
// Mobile-optimised view that REPLACES MspOsApp on phone/iPad after login.
// Lives in the same bundle — reuses the same handler props and atReference data.
//
// Three screens:
//   1. list   — open tickets assigned to me (excl. status 5 & 32)
//   2. ticket — edit existing OR create new (mobile-optimised, same field model)
//   3. time   — Who / Issue / Resolution → combined into one summaryNotes
//
// Props (subset of what MspOsApp receives, plus a `timelog` bundle):
//   userInfo, atReference, syncedCompanies, onSignOut,
//   onCreateTicket, onGetContacts, onGetContracts, onGetAssets, onLoadAtCompanies,
//   timelog: { getMyTickets, getTicketDetail, updateTicket, createTimeEntry, suggestIssueSummary }

import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";

// Pull the most specific error text available: backend {error} body first,
// then axios message. A bare "status code 500" hides Autotask's real reason.
function errText(e) {
  return e?.response?.data?.error || e?.response?.data?.message || e?.message || "error";
}

const css = `
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap');
.mob * { box-sizing: border-box; -webkit-tap-highlight-color: transparent; }
.mob {
  --navy:#2a4365; --teal:#0b80a5; --bg:#f4f6f8; --card:#ffffff;
  --line:#e3e8ec; --text:#1f2933; --muted:#6b7682;
  --ok:#27ae60; --warn:#e67e22; --danger:#e74c3c; --radius:14px;
  font-family:'Inter',sans-serif; color:var(--text);
  position:fixed; inset:0; background:var(--bg);
  display:flex; flex-direction:column;
  padding-top:env(safe-area-inset-top); padding-bottom:env(safe-area-inset-bottom);
}
.mob-top {
  background:linear-gradient(135deg,var(--navy),var(--teal));
  color:#fff; padding:16px 18px 14px; flex-shrink:0;
  display:flex; align-items:center; gap:12px;
}
.mob-top .back { background:rgba(255,255,255,.16); border:none; color:#fff;
  width:34px; height:34px; border-radius:9px; font-size:18px; cursor:pointer; flex-shrink:0; }
.mob-top h1 { font-size:17px; font-weight:700; letter-spacing:-.02em; margin:0; flex:1; }
.mob-top .sub { font-size:11px; opacity:.75; font-weight:500; margin-top:2px; }
.mob-top .who { font-size:11px; opacity:.85; text-align:right; }
.mob-top .who button { background:none;border:none;color:#fff;opacity:.7;font-size:10px;
  text-decoration:underline;cursor:pointer;padding:0;margin-top:2px; }
.mob-top .refresh { background:rgba(255,255,255,.16); border:none; color:#fff;
  width:34px; height:34px; border-radius:9px; font-size:16px; cursor:pointer; flex-shrink:0; }
.mob-top .refresh.spinning { animation:mspin .8s linear infinite; }
.ptr { display:flex; align-items:center; justify-content:center; gap:8px; overflow:hidden;
  color:var(--muted); font-size:12px; font-weight:600; transition:height .15s ease, opacity .15s; }
.ptr .spinner { width:14px; height:14px; }
/* bottom tab bar */
.tabbar { flex-shrink:0; display:flex; border-top:1px solid var(--line); background:#fff;
  padding-bottom:env(safe-area-inset-bottom); }
.tabbar button { flex:1; background:none; border:none; cursor:pointer; font-family:inherit;
  padding:10px 0 9px; font-size:11px; font-weight:600; color:var(--muted);
  display:flex; flex-direction:column; align-items:center; gap:3px; }
.tabbar button.on { color:var(--teal); }
.tabbar button .ti { font-size:19px; line-height:1; }
/* My Work sub-tab bar */
.subtabbar { display:flex; border-top:1px solid var(--line); background:#fff; flex-shrink:0; }
/* 11px + nowrap since the third tab landed: three labels at 12px overflow a
   320px phone, and without nowrap they wrap and double the bar's height. */
.subtabbar button { flex:1; background:none; border:none; cursor:pointer; font-family:inherit;
  padding:10px 0; font-size:11px; font-weight:600; color:var(--muted);
  white-space:nowrap; border-bottom:2px solid transparent; }
.subtabbar button.on { color:var(--teal); border-bottom-color:var(--teal); }
/* My ToDo list */
.todo-add { display:flex; gap:8px; margin-bottom:14px; }
.todo-add input { flex:1; background:#fff; border:1px solid #d6dde2; border-radius:11px;
  padding:12px 14px; font-size:16px; font-family:inherit; color:var(--text); outline:none; }
.todo-add input:focus { border-color:var(--teal); }
.todo-add button { background:var(--teal); color:#fff; border:none; border-radius:11px;
  width:44px; font-size:22px; cursor:pointer; font-weight:700; flex-shrink:0; }
.todo-add button:disabled { opacity:.4; }
.todo-card { display:flex; align-items:flex-start; gap:10px; background:var(--card);
  border:1px solid var(--line); border-radius:var(--radius); padding:12px 14px;
  margin-bottom:8px; cursor:pointer; }
.todo-card.done { opacity:.6; }
.todo-check { background:none; border:2px solid var(--line); border-radius:50%; width:26px; height:26px;
  flex-shrink:0; cursor:pointer; font-size:14px; display:flex; align-items:center;
  justify-content:center; color:var(--muted); }
.todo-check.done { background:var(--teal); border-color:var(--teal); color:#fff; }
.todo-body { flex:1; min-width:0; }
.todo-text { font-size:14px; font-weight:600; color:var(--text); line-height:1.4; word-break:break-word; }
.todo-meta { font-size:11px; color:var(--muted); margin-top:3px; }
.todo-chevron { color:var(--muted); font-size:18px; flex-shrink:0; align-self:center; }
.todo-done-section { margin-top:16px; }
.todo-done-section summary { font-size:12px; font-weight:700; color:var(--muted);
  text-transform:uppercase; letter-spacing:.04em; cursor:pointer; padding:8px 2px; }
/* customer picker */
.cust-pick { padding:14px 14px 0; flex-shrink:0; }
.cust-pick select { width:100%; background:#fff; border:1px solid #d6dde2; border-radius:11px;
  padding:13px 14px; font-size:16px; font-family:inherit; color:var(--text); outline:none; }
.cust-pick select:focus { border-color:var(--teal); }
/* read-only detail */
.detail-info { background:var(--card); border:1px solid var(--line); border-radius:var(--radius);
  padding:14px 15px; margin-bottom:14px; }
.detail-info .row { display:flex; gap:8px; font-size:12px; margin-top:6px; color:var(--muted); }
.detail-info .row b { color:var(--text); font-weight:600; }
.detail-desc { font-size:13px; line-height:1.5; margin-top:10px; white-space:pre-line;
  overflow-wrap:anywhere; border-top:1px solid var(--line); padding-top:10px; }
.sec-title { font-size:11px; font-weight:700; color:var(--muted); text-transform:uppercase;
  letter-spacing:.05em; margin:18px 2px 8px; }
.note-card, .te-card { background:var(--card); border:1px solid var(--line);
  border-radius:11px; padding:11px 13px; margin-bottom:8px; }
.note-card .meta, .te-card .meta { font-size:10px; color:var(--muted); font-weight:600;
  display:flex; justify-content:space-between; gap:8px; margin-bottom:5px; }
.note-card .body { font-size:13px; line-height:1.5; white-space:pre-line; overflow-wrap:anywhere; }
.te-card .body { font-size:13px; line-height:1.5; white-space:pre-line; overflow-wrap:anywhere; }
.te-card .hrs { color:var(--teal); font-weight:700; }
.mob-body * { word-break:break-word; overflow-wrap:anywhere; }
.detail-fields { border-top:1px solid var(--line); margin-top:12px; padding-top:10px; }
.df-row { display:flex; justify-content:space-between; align-items:flex-start; gap:12px;
  padding:7px 0; border-bottom:1px solid var(--line); }
.df-row:last-child { border-bottom:none; }
.df-label { font-size:11px; font-weight:700; color:var(--muted); text-transform:uppercase;
  letter-spacing:.04em; flex-shrink:0; }
.df-val { font-size:13px; font-weight:500; color:var(--text); text-align:right;
  word-break:break-word; overflow-wrap:anywhere; }
/* completion modal */
.modal-back { position:fixed; inset:0; background:rgba(15,20,25,.5); z-index:60;
  display:flex; align-items:flex-end; justify-content:center; padding:0;
  padding-bottom:env(safe-area-inset-bottom); }
.modal { background:#fff; width:100%; max-width:540px; border-radius:18px 18px 0 0;
  padding:20px 18px calc(18px + env(safe-area-inset-bottom)); }
.modal h3 { margin:0 0 4px; font-size:18px; font-weight:800; color:var(--text); }
.modal p { margin:0 0 14px; font-size:13px; color:var(--muted); line-height:1.5; }
.modal textarea { width:100%; background:#fff; border:1px solid #d6dde2; border-radius:11px;
  padding:13px 14px; font-size:16px; font-family:inherit; color:var(--text); outline:none;
  resize:vertical; min-height:90px; line-height:1.5; }
.modal textarea:focus { border-color:var(--teal); }
.modal-actions { display:flex; gap:10px; margin-top:16px; }
.modal-actions button { flex:1; border-radius:11px; padding:14px; font-size:15px;
  font-weight:700; cursor:pointer; font-family:inherit; border:1px solid var(--line); }
.modal-actions .cancel { background:#f4f6f8; color:var(--text); }
.modal-actions .confirm { background:var(--teal); color:#fff; border-color:var(--teal); }
.mob-body { flex:1; overflow-y:auto; -webkit-overflow-scrolling:touch; padding:14px; }
/* Replaced the old .mob-fab. As a floating button it sat bottom-right over the
   Unassigned sub-tab label; inline at the top of the list it covers nothing.
   The old class had exactly one usage, so it is gone rather than left dead. */
.btn-new-ticket {
  display:flex; align-items:center; justify-content:center; gap:8px;
  width:100%; margin-bottom:14px;
  background:linear-gradient(135deg,var(--navy),var(--teal)); color:#fff; border:none;
  border-radius:12px; padding:14px 20px; font-size:15px; font-weight:700;
  cursor:pointer; font-family:inherit;
}
.btn-new-ticket:active { opacity:.88; }

/* list */
.tk { background:var(--card); border:1px solid var(--line); border-radius:var(--radius);
  padding:14px 15px; margin-bottom:10px; cursor:pointer; transition:border-color .12s; }
.tk:active { border-color:var(--teal); }
.tk-top { display:flex; align-items:center; gap:8px; margin-bottom:5px; }
.tk-num { font-size:12px; font-weight:700; color:var(--teal); }
.tk-pri { font-size:9px; font-weight:700; padding:2px 7px; border-radius:999px; text-transform:uppercase; letter-spacing:.04em; margin-left:auto; }
.tk-pri.p1 { background:#fdecea; color:#c0392b; } .tk-pri.p2 { background:#fef5e7; color:#b9770e; }
.tk-pri.p3 { background:#eaf2f8; color:#2471a3; } .tk-pri.p4 { background:#f4f6f7; color:#7b8a8b; }
.tk-title { font-size:14px; font-weight:600; line-height:1.35; margin-bottom:7px; }
.tk-meta { font-size:11px; color:var(--muted); display:flex; gap:6px; flex-wrap:wrap; }
.tk-meta b { color:var(--text); font-weight:600; }
.tk-hrs { margin-left:auto; color:#0c8599; font-weight:600; }
.tk-actions { display:flex; gap:8px; margin-top:11px; }
.tk-actions button { flex:1; border:1px solid var(--line); background:#f8fafb; color:var(--text);
  border-radius:9px; padding:9px 0; font-size:12px; font-weight:600; cursor:pointer; font-family:inherit; }
.tk-actions button.go { background:var(--teal); color:#fff; border-color:var(--teal); }

/* form */
.f { margin-bottom:15px; }
.f label { display:block; font-size:10px; font-weight:700; color:var(--muted);
  text-transform:uppercase; letter-spacing:.05em; margin:0 0 6px 2px; }
.f input, .f select, .f textarea {
  width:100%; background:#fff; border:1px solid #d6dde2; border-radius:11px;
  padding:13px 14px; font-size:16px; font-family:inherit; color:var(--text); outline:none; }
.f input:focus, .f select:focus, .f textarea:focus { border-color:var(--teal);
  box-shadow:0 0 0 3px rgba(11,128,165,.12); }
.f textarea { resize:vertical; min-height:70px; line-height:1.5; }
.f select:disabled { background:#f1f3f5; color:var(--muted); }
.f-row { display:grid; grid-template-columns:1fr 1fr; gap:10px; }
.req { color:var(--danger); }
.hint { font-size:10px; color:var(--muted); margin-top:4px; margin-left:2px; }
.ai-chip { display:inline-flex; align-items:center; gap:5px; font-size:10px; font-weight:600;
  color:var(--teal); background:rgba(11,128,165,.08); border:1px solid rgba(11,128,165,.2);
  border-radius:999px; padding:3px 9px; margin-left:8px; cursor:pointer; }
.ai-chip.spin { opacity:.6; }

/* hours */
.hours { display:flex; gap:8px; margin-bottom:10px; }
.hours .chip { flex:1; background:#fff; border:1px solid #d6dde2; color:var(--text);
  border-radius:11px; padding:13px 0; font-weight:700; font-size:15px; cursor:pointer; }
.hours .chip.sel { background:var(--teal); color:#fff; border-color:var(--teal); }

/* footer button */
.mob-foot { flex-shrink:0; padding:12px 14px calc(14px + env(safe-area-inset-bottom));
  border-top:1px solid var(--line); background:#fff; }
.btn-primary { width:100%; background:linear-gradient(135deg,var(--navy),var(--teal)); color:#fff;
  border:none; border-radius:12px; padding:16px; font-size:16px; font-weight:700; cursor:pointer; font-family:inherit; }
.btn-primary:disabled { opacity:.4; }
.btn-primary:active:not(:disabled) { transform:scale(.99); }

.empty { text-align:center; color:var(--muted); font-size:13px; padding:40px 20px; line-height:1.6; }
.spinner { width:16px; height:16px; border:2px solid currentColor; border-top-color:transparent;
  border-radius:50%; animation:mspin .7s linear infinite; display:inline-block; vertical-align:-2px; }
@keyframes mspin { to { transform:rotate(360deg); } }
.toast { position:fixed; left:50%; bottom:calc(80px + env(safe-area-inset-bottom));
  transform:translateX(-50%) translateY(20px); opacity:0; pointer-events:none; z-index:50;
  background:var(--navy); color:#fff; padding:12px 20px; border-radius:999px; font-size:13px;
  font-weight:600; transition:.25s; max-width:90%; }
.toast.show { opacity:1; transform:translateX(-50%) translateY(0); }
.toast.ok { background:var(--ok); } .toast.err { background:var(--danger); }
`;

const QUICK_HOURS = [0.25, 0.5, 1, 2];
const PRIORITY_LABELS = { 1: "Critical", 2: "High", 3: "Medium", 4: "Low" };

// Status NAME → colour for ticket cards. Autotask doesn't expose status colours
// via API, so we match by exact status name (case-insensitive). Statuses not
// listed here render plain black. Adjust the keys to your instance's exact names.
const STATUS_COLORS = {
  "new": "#e03131",                 // red
  "customer note added": "#7048e8", // purple
  "waiting customer": "#e8920c",    // amber
  "awaiting time entry": "#e64980", // rose
  "in progress": "#d9480f",         // dark orange
  "on hold": "#4dabf7",             // sky blue
};

export default function MspOsMobile(props) {
  const {
    userInfo, atReference, syncedCompanies = [], onSignOut,
    onCreateTicket, onAddTicketNote, onGetContacts, onGetContracts, onGetAssets, onLoadAtCompanies,
    timelog = {}, itglue = {},
  } = props;

  const [screen, setScreen] = useState("list");   // list | ticket | time | custDetail | notes | timeEntries
  const [tab, setTab] = useState("mine");          // mine | customer | itglue (bottom tab bar)
  const [workSubTab, setWorkSubTab] = useState("tickets"); // "tickets" | "todos" | "unassigned" (My Work sub-nav)
  const [tickets, setTickets] = useState(null);    // null = loading (My Tickets)
  const [activeTicket, setActiveTicket] = useState(null); // ticket being logged/viewed
  const [toast, setToastState] = useState({ msg: "", kind: "" });

  // My ToDo state
  const [todos, setTodos] = useState(null);        // null = not loaded yet
  const [todosLoading, setTodosLoading] = useState(false);
  // Unassigned state — null = not loaded yet (drives the loading placeholder)
  const [unassignedTickets, setUnassignedTickets] = useState(null);
  // Prefill + origin tracking for "create ticket from todo"
  const [ticketPrefill, setTicketPrefill] = useState(null);
  const [activeTodoId, setActiveTodoId] = useState(null);

  // Customer View state
  const [custCompanyId, setCustCompanyId] = useState("");
  const [custTickets, setCustTickets] = useState(null); // null = not loaded yet
  const [activeNotes, setActiveNotes] = useState(null);    // notes/entries lifted from
  const [activeEntries, setActiveEntries] = useState(null); // TicketDetailScreen so sub-screens don't re-fetch

  const showToast = useCallback((msg, kind = "") => {
    setToastState({ msg, kind, show: true });
    setTimeout(() => setToastState(s => ({ ...s, show: false })), 2600);
  }, []);

  // ── company/contact name resolution from reference data ──────────────────
  const companies = useMemo(
    () => (syncedCompanies.length ? syncedCompanies : (atReference?.companies || [])),
    [syncedCompanies, atReference],
  );
  const companyName = useCallback((id) => {
    const c = companies.find(x => String(x.id) === String(id));
    return c?.companyName || c?.name || (id ? `Company ${id}` : "—");
  }, [companies]);

  // Resolve a ticket's status id → { name, color } for the coloured card label.
  // color is null (→ black) for statuses not in STATUS_COLORS.
  const statusBadge = useCallback((statusId) => {
    const s = (atReference?.statuses || []).find(x => String(x.id ?? x.value) === String(statusId));
    const name = s ? (s.name || s.label) : null;
    const color = name ? (STATUS_COLORS[name.trim().toLowerCase()] || null) : null;
    return { name, color };
  }, [atReference]);

  // ── load my tickets ──────────────────────────────────────────────────────
  // keepVisible=true (used by pull/tap refresh) leaves the current list on
  // screen while fetching, so the refresh spinner shows instead of a full blank.
  const loadTickets = useCallback(async (keepVisible = false) => {
    if (!keepVisible) setTickets(null);
    try {
      const list = await timelog.getMyTickets();
      setTickets(list);
    } catch (e) {
      if (!keepVisible) setTickets([]);
      showToast("Couldn't load tickets", "err");
    }
  }, [timelog, showToast]);

  // ── load open tickets nobody owns ─────────────────────────────────────────
  // Same keepVisible contract as loadTickets: a tap-refresh keeps the current
  // list on screen so the ↻ spinner reads as a refresh, not a reload.
  const loadUnassigned = useCallback(async (keepVisible = false) => {
    if (!keepVisible) setUnassignedTickets(null);
    try {
      const list = await timelog.getUnassignedTickets?.();
      setUnassignedTickets(list || []);
    } catch (e) {
      setUnassignedTickets([]);
      showToast("Couldn't load unassigned tickets", "err");
    }
  }, [timelog, showToast]);

  // ── load a customer's open tickets ────────────────────────────────────────
  const loadCustomerTickets = useCallback(async (companyId, keepVisible = false) => {
    if (!companyId) { setCustTickets(null); return; }
    if (!keepVisible) setCustTickets(null);
    try {
      const list = await timelog.getCompanyTickets(companyId);
      setCustTickets(list);
    } catch (e) {
      setCustTickets([]);
      showToast("Couldn't load customer tickets", "err");
    }
  }, [timelog, showToast]);

  useEffect(() => { loadTickets(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (companies.length === 0 && onLoadAtCompanies) onLoadAtCompanies(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── My ToDo: load on first open of the sub-tab, then keep in local state ────
  useEffect(() => {
    if (workSubTab === "todos" && todos === null && !todosLoading) {
      setTodosLoading(true);
      Promise.resolve(timelog.getTodos?.() ?? [])
        .then(items => setTodos(items || []))
        .catch(() => { setTodos([]); showToast("Couldn't load todos", "err"); })
        .finally(() => setTodosLoading(false));
    }
  }, [workSubTab]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Unassigned: load on first open of the sub-tab, then keep in local state ─
  // Only when still null, so switching away and back doesn't re-hit Autotask —
  // the ↻ button is the deliberate refresh.
  useEffect(() => {
    if (workSubTab === "unassigned" && unassignedTickets === null) loadUnassigned();
  }, [workSubTab]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleToggleTodo = async (id) => {
    // Optimistic flip; roll back on failure.
    setTodos(prev => (prev || []).map(t => (t.id === id ? { ...t, done: !t.done } : t)));
    try {
      await timelog.toggleTodo?.(id);
    } catch (e) {
      setTodos(prev => (prev || []).map(t => (t.id === id ? { ...t, done: !t.done } : t)));
      showToast("Couldn't update todo", "err");
    }
  };

  const handleAddTodo = async (title) => {
    try {
      const created = await timelog.addTodo?.(title);
      setTodos(prev => [...(prev || []), created || {
        id: `td-${Date.now()}`, title, notes: "", dueDate: "", priority: "med",
        layer: "O", client: "", done: false, source: "Manual",
        createdAt: new Date().toISOString(),
      }]);
    } catch (e) { showToast("Couldn't add todo", "err"); }
  };

  // ── navigation helpers ────────────────────────────────────────────────────
  const openTime = (ticket) => { setActiveTicket(ticket); setScreen("time"); };
  const openEdit = (ticket) => { setActiveTicket(ticket); setScreen("ticket"); };
  // prefill/fromTodoId set only when creating a ticket from a todo
  const openCreate = (prefill = null, fromTodoId = null) => {
    setTicketPrefill(prefill);
    setActiveTodoId(fromTodoId);
    setActiveTicket(null);
    setScreen("ticket");
  };
  const openCustDetail = (ticket) => { setActiveTicket(ticket); setScreen("custDetail"); };
  const backToBase = () => { setScreen("list"); setActiveTicket(null); setTicketPrefill(null); setActiveTodoId(null); };

  const onSelectCustomer = (companyId) => {
    setCustCompanyId(companyId);
    loadCustomerTickets(companyId);
  };

  const atBase = screen === "list"; // base screens show the bottom tab bar

  return (
    <div className="mob">
      <style>{css}</style>

      {atBase && tab === "mine" && workSubTab === "tickets" && (
        <ListScreen
          tickets={tickets} companyName={companyName} statusBadge={statusBadge}
          onReload={() => loadTickets(true)} onView={openCustDetail} onTime={openTime}
          onCreate={() => openCreate()} userInfo={userInfo} onSignOut={onSignOut}
        />
      )}

      {atBase && tab === "mine" && workSubTab === "todos" && (
        <TodoScreen
          todos={todos} loading={todosLoading}
          onToggleTodo={handleToggleTodo} onAddTodo={handleAddTodo}
          onCreateTicketFromTodo={(todo) => openCreate(
            {
              prefillTitle: todo.title || "",
              prefillDescription: todo.notes || todo.desc
                ? `${todo.notes || todo.desc}${todo.client ? `\n\nClient: ${todo.client}` : ""}`
                : (todo.client ? `Client: ${todo.client}` : ""),
            },
            todo.id,
          )}
        />
      )}

      {atBase && tab === "mine" && workSubTab === "unassigned" && (
        <UnassignedScreen
          tickets={unassignedTickets} companyName={companyName} statusBadge={statusBadge}
          onReload={() => loadUnassigned(true)} onOpen={openCustDetail} onTime={openTime}
        />
      )}

      {atBase && tab === "customer" && (
        <CustomerScreen
          companies={companies} companyName={companyName} statusBadge={statusBadge}
          selectedId={custCompanyId} tickets={custTickets}
          onSelectCustomer={onSelectCustomer}
          onReload={() => loadCustomerTickets(custCompanyId, true)}
          onOpen={openCustDetail} onTime={openTime}
        />
      )}

      {atBase && tab === "itglue" && (
        <ITGlueScreen
          itglue={itglue} companies={companies} companyName={companyName}
          initialCompanyId={custCompanyId}
          onExit={() => setTab("mine")} showToast={showToast}
        />
      )}

      {screen === "ticket" && (
        <TicketScreen
          existing={activeTicket} atReference={atReference} companies={companies}
          prefill={ticketPrefill}
          onGetContacts={onGetContacts} onGetContracts={onGetContracts} onGetAssets={onGetAssets}
          onCreateTicket={onCreateTicket} updateTicket={timelog.updateTicket}
          onAddTicketNote={onAddTicketNote}
          getSecondaryResources={timelog.getSecondaryResources}
          addSecondaryResource={timelog.addSecondaryResource}
          onBack={backToBase}
          onSaved={(savedTicket, isNew) => {
            showToast(isNew ? "Ticket created" : "Ticket updated", "ok");
            // If this ticket was created from a todo, mark that todo done (mirrors
            // the web app moving a ticketed todo into Completed).
            if (isNew && activeTodoId) {
              const todoId = activeTodoId;
              setTodos(prev => (prev || []).map(t => (t.id === todoId ? { ...t, done: true } : t)));
              timelog.toggleTodo?.(todoId).catch(() => {});
              setActiveTodoId(null);
            }
            setTicketPrefill(null);
            loadTickets();
            if (isNew && savedTicket) { setActiveTicket(savedTicket); setScreen("time"); }
            else backToBase();
          }}
          showToast={showToast}
        />
      )}

      {screen === "custDetail" && activeTicket && (
        <TicketDetailScreen
          ticket={activeTicket} companyName={companyName} timelog={timelog}
          atReference={atReference}
          onBack={() => setScreen("list")}
          onEdit={() => openEdit(activeTicket)}
          onViewNotes={() => setScreen("notes")}
          onViewTime={() => setScreen("timeEntries")}
          onNotesLoaded={setActiveNotes}
          onEntriesLoaded={setActiveEntries}
        />
      )}

      {screen === "notes" && activeTicket && (
        <TicketNotesScreen
          ticket={activeTicket} notes={activeNotes}
          onBack={() => setScreen("custDetail")}
        />
      )}

      {screen === "timeEntries" && activeTicket && (
        <TicketTimeScreen
          ticket={activeTicket} entries={activeEntries}
          onBack={() => setScreen("custDetail")}
        />
      )}

      {screen === "time" && activeTicket && (
        <TimeScreen
          ticket={activeTicket} companyName={companyName} atReference={atReference}
          suggestIssueSummary={timelog.suggestIssueSummary}
          createTimeEntry={timelog.createTimeEntry}
          updateTicket={timelog.updateTicket}
          onBack={backToBase}
          onLogged={() => {
            showToast("Time logged ✓", "ok");
            // refresh whichever list we came from
            loadTickets(true);
            if (custCompanyId) loadCustomerTickets(custCompanyId, true);
            backToBase();
          }}
          showToast={showToast}
        />
      )}

      {/* My Work sub-tab bar — sits directly above the main tab bar */}
      {atBase && tab === "mine" && (
        <div className="subtabbar">
          <button className={workSubTab === "tickets" ? "on" : ""} onClick={() => setWorkSubTab("tickets")}>
            🎫 My Tickets
          </button>
          <button className={workSubTab === "todos" ? "on" : ""} onClick={() => setWorkSubTab("todos")}>
            ✅ My ToDo
          </button>
          <button className={workSubTab === "unassigned" ? "on" : ""} onClick={() => setWorkSubTab("unassigned")}>
            📋 Unassigned
          </button>
        </div>
      )}

      {atBase && tab !== "itglue" && (
        <nav className="tabbar">
          <button className={tab === "mine" ? "on" : ""} onClick={() => setTab("mine")}>
            <span className="ti">💼</span>My Work
          </button>
          <button className={tab === "customer" ? "on" : ""} onClick={() => setTab("customer")}>
            <span className="ti">🏢</span>Customer View
          </button>
          <button className={tab === "itglue" ? "on" : ""} onClick={() => setTab("itglue")}>
            <span className="ti">🗄</span>IT Glue
          </button>
        </nav>
      )}

      <div className={`toast ${toast.show ? "show" : ""} ${toast.kind}`}>{toast.msg}</div>
    </div>
  );
}

// ─── Screen 1: ticket list ───────────────────────────────────────────────────
function ListScreen({ tickets, companyName, statusBadge, onReload, onView, onTime, onCreate, userInfo, onSignOut }) {
  const [refreshing, setRefreshing] = useState(false);
  const [pull, setPull] = useState(0);        // px pulled down (visual)
  const startY = useRef(null);
  const bodyRef = useRef(null);
  const THRESHOLD = 70;                         // px to trigger a refresh

  const doRefresh = useCallback(async () => {
    if (refreshing) return;
    setRefreshing(true);
    try { await onReload(); } finally {
      setRefreshing(false);
      setPull(0);
    }
  }, [refreshing, onReload]);

  // Pull-to-refresh: only engage when the list is scrolled to the very top.
  const onTouchStart = (e) => {
    if (bodyRef.current && bodyRef.current.scrollTop <= 0) startY.current = e.touches[0].clientY;
    else startY.current = null;
  };
  const onTouchMove = (e) => {
    if (startY.current == null || refreshing) return;
    const dy = e.touches[0].clientY - startY.current;
    if (dy > 0) {
      // Resist: show a fraction of the drag, cap it.
      setPull(Math.min(dy * 0.5, THRESHOLD + 20));
    }
  };
  const onTouchEnd = () => {
    if (startY.current == null) return;
    if (pull >= THRESHOLD) doRefresh();
    else setPull(0);
    startY.current = null;
  };

  return (
    <>
      <div className="mob-top">
        <div style={{ flex: 1 }}>
          <h1>My Tickets</h1>
          <div className="sub">Open tickets assigned to you</div>
        </div>
        <button className={`refresh ${refreshing ? "spinning" : ""}`} onClick={doRefresh}
          aria-label="Refresh" title="Refresh">↻</button>
        <div className="who">
          {userInfo?.name || userInfo?.username || ""}
          <br /><button onClick={onSignOut}>Sign out</button>
        </div>
      </div>

      <div className="mob-body" ref={bodyRef}
        onTouchStart={onTouchStart} onTouchMove={onTouchMove} onTouchEnd={onTouchEnd}>
        <div className="ptr" style={{ height: refreshing ? 34 : pull, opacity: (refreshing || pull) ? 1 : 0 }}>
          {refreshing
            ? <><span className="spinner" /> Refreshing…</>
            : pull >= THRESHOLD ? "Release to refresh" : pull > 0 ? "Pull to refresh" : ""}
        </div>
        {/* Inline, not a FAB: the floating version sat bottom-right over the
            Unassigned sub-tab label and made it unreadable. */}
        <button className="btn-new-ticket" onClick={onCreate}>＋ New ticket</button>
        {tickets === null && <div className="empty"><span className="spinner" /> Loading tickets…</div>}
        {tickets && tickets.length === 0 && (
          <div className="empty">No open tickets assigned to you.<br />Tap ＋ New ticket above to create one.</div>
        )}
        {tickets && tickets.map(t => (
          <div className="tk" key={t.id}>
            <div className="tk-top" onClick={() => onView(t)}>
              <span className="tk-num">#{t.ticketNumber || t.id}</span>
              <span className={`tk-pri p${t.priority || 4}`}>{PRIORITY_LABELS[t.priority] || "—"}</span>
              {(() => { const b = statusBadge(t.status); return b.name ? (
                <span style={{ fontSize: 11, fontWeight: 700, color: b.color || "#1f2933" }}>{b.name}</span>
              ) : null; })()}
            </div>
            <div className="tk-title" onClick={() => onView(t)}>{t.title || "(no title)"}</div>
            <div className="tk-meta">
              <span><b>{companyName(t.companyID)}</b></span>
              {t.contactName && <span>· {t.contactName}</span>}
              {t.hoursLogged > 0 && <span className="tk-hrs">⏱ {Math.round(t.hoursLogged * 4) / 4}h</span>}
            </div>
            <div className="tk-actions">
              <button onClick={() => onView(t)}>👁 View</button>
              <button className="go" onClick={() => onTime(t)}>⏱ Log Time</button>
            </div>
          </div>
        ))}
      </div>

    </>
  );
}

// ─── My ToDo: list open/done todos from app-state; add, complete, ticket ─────
// Todo shape (from MspOsApp addManualTodo): { id, title, notes, dueDate,
// priority:"high"|"med"|"low", layer, client, done, source, createdAt,
// atTicketId, atTicketNumber } — cadence-sourced todos use `desc` for `notes`.
const TODO_PRIORITY_WORD = { high: "High", med: "Medium", low: "Low" };
function todoIsTicketed(t) { return !!(t.atTicketId || t.atTicketNumber); }

function TodoScreen({ todos, loading, onToggleTodo, onAddTodo, onCreateTicketFromTodo }) {
  const [addText, setAddText] = useState("");
  const [adding, setAdding] = useState(false);
  const [selected, setSelected] = useState(null); // todo detail view

  const openTodos = (todos || []).filter(t => !t.done);
  const doneTodos = (todos || []).filter(t => t.done);

  const submitAdd = async () => {
    const text = addText.trim();
    if (!text) return;
    setAdding(true);
    await onAddTodo(text);
    setAddText("");
    setAdding(false);
  };

  if (selected) {
    return (
      <TodoDetailScreen
        todo={selected}
        ticketed={todoIsTicketed(selected)}
        onBack={() => setSelected(null)}
        onToggle={() => { onToggleTodo(selected.id); setSelected(null); }}
        onCreateTicket={() => onCreateTicketFromTodo(selected)}
      />
    );
  }

  return (
    <>
      <div className="mob-top">
        <div style={{ flex: 1 }}>
          <h1>My ToDo</h1>
          <div className="sub">
            {loading ? "Loading…"
              : `${openTodos.length} open${doneTodos.length ? ` · ${doneTodos.length} done` : ""}`}
          </div>
        </div>
      </div>

      <div className="mob-body">
        <div className="todo-add">
          <input type="text" placeholder="Add a new todo…" value={addText}
            onChange={e => setAddText(e.target.value)}
            onKeyDown={e => e.key === "Enter" && submitAdd()} />
          <button onClick={submitAdd} disabled={adding || !addText.trim()}>{adding ? "…" : "＋"}</button>
        </div>

        {loading && <div className="empty"><span className="spinner" /> Loading…</div>}
        {!loading && openTodos.length === 0 && <div className="empty">No open todos. 🎉</div>}

        {openTodos.map(t => (
          <div className="todo-card" key={t.id} onClick={() => setSelected(t)}>
            <button className="todo-check" onClick={e => { e.stopPropagation(); onToggleTodo(t.id); }}>○</button>
            <div className="todo-body">
              <div className="todo-text">{t.title}</div>
              {t.client && <div className="todo-meta">🏢 {t.client}</div>}
              {todoIsTicketed(t) && (
                <div className="todo-meta" style={{ color: "var(--teal)" }}>
                  🎫 {t.atTicketNumber || t.atTicketNum || "Ticket created"}
                </div>
              )}
              <div className="todo-meta">
                {t.dueDate ? `Due ${t.dueDate}`
                  : t.createdAt ? new Date(t.createdAt).toLocaleDateString(undefined, { day: "numeric", month: "short" })
                  : ""}
              </div>
            </div>
            <span className="todo-chevron">›</span>
          </div>
        ))}

        {doneTodos.length > 0 && (
          <details className="todo-done-section">
            <summary>Completed ({doneTodos.length})</summary>
            {doneTodos.map(t => (
              <div className="todo-card done" key={t.id} onClick={() => setSelected(t)}>
                <button className="todo-check done" onClick={e => { e.stopPropagation(); onToggleTodo(t.id); }}>✓</button>
                <div className="todo-body"><div className="todo-text">{t.title}</div></div>
              </div>
            ))}
          </details>
        )}
      </div>
    </>
  );
}

function TodoDetailScreen({ todo, ticketed, onBack, onToggle, onCreateTicket }) {
  const notes = todo.notes || todo.desc || "";
  return (
    <>
      <div className="mob-top">
        <button className="back" onClick={onBack}>‹</button>
        <div style={{ flex: 1 }}><h1>ToDo Detail</h1></div>
      </div>

      <div className="mob-body">
        <div className="detail-info">
          <div style={{ fontSize: 16, fontWeight: 600, lineHeight: 1.4,
            textDecoration: todo.done ? "line-through" : "none",
            color: todo.done ? "var(--muted)" : "var(--text)" }}>
            {todo.title}
          </div>

          {notes && <div className="detail-desc" style={{ marginTop: 8 }}>{notes}</div>}

          {todo.client && <div className="row" style={{ marginTop: 10 }}><span>Client:</span><b>{todo.client}</b></div>}
          {todo.dueDate && <div className="row"><span>Due:</span><b>{todo.dueDate}</b></div>}
          {todo.priority && <div className="row"><span>Priority:</span><b>{TODO_PRIORITY_WORD[todo.priority] || todo.priority}</b></div>}
          {todo.createdAt && (
            <div className="row"><span>Created:</span>
              <b>{new Date(todo.createdAt).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" })}</b>
            </div>
          )}
          {todo.source && <div className="row"><span>Source:</span><b>{todo.source}</b></div>}

          {ticketed && (
            <div style={{ marginTop: 12, fontSize: 12, color: "var(--teal)", fontWeight: 700 }}>
              🎫 Ticket {todo.atTicketNumber || todo.atTicketNum || "created"} — a ticket already exists for this todo.
            </div>
          )}
        </div>
      </div>

      <div className="mob-foot" style={{ display: "flex", gap: 8 }}>
        <button className="btn-secondary" onClick={onToggle}>{todo.done ? "↩ Reopen" : "✓ Complete"}</button>
        <button className="btn-primary" onClick={onCreateTicket} disabled={ticketed}>
          {ticketed ? "Ticket exists" : "🎫 Create ticket"}
        </button>
      </div>
    </>
  );
}

// ─── Customer View: pick a company → its open tickets ────────────────────────
// ─── Unassigned: open tickets nobody owns ───────────────────────────────────
// Same card as ListScreen/CustomerScreen, no create FAB — these are existing
// tickets looking for an owner. To take one, open it and use Edit's assignToMe;
// it then leaves this list and appears in My Tickets on the next refresh.
function UnassignedScreen({ tickets, companyName, statusBadge, onReload, onOpen, onTime }) {
  const [refreshing, setRefreshing] = useState(false);

  const doRefresh = async () => {
    if (refreshing) return;
    setRefreshing(true);
    try { await onReload(); } finally { setRefreshing(false); }
  };

  return (
    <>
      <div className="mob-top">
        <div style={{ flex: 1 }}>
          <h1>Unassigned</h1>
          <div className="sub">
            Open tickets with no assigned tech
            {tickets && tickets.length > 0 ? ` · ${tickets.length}` : ""}
          </div>
        </div>
        <button className={`refresh ${refreshing ? "spinning" : ""}`}
                onClick={doRefresh} aria-label="Refresh" title="Refresh">↻</button>
      </div>

      <div className="mob-body">
        {tickets === null && <div className="empty"><span className="spinner" /> Loading tickets…</div>}
        {tickets && tickets.length === 0 && <div className="empty">No unassigned open tickets. ✓</div>}
        {tickets && tickets.map(t => (
          <div className="tk" key={t.id}>
            <div className="tk-top" onClick={() => onOpen(t)}>
              <span className="tk-num">#{t.ticketNumber || t.id}</span>
              <span className={`tk-pri p${t.priority || 4}`}>{PRIORITY_LABELS[t.priority] || "—"}</span>
              {(() => { const b = statusBadge(t.status); return b.name ? (
                <span style={{ fontSize: 11, fontWeight: 700, color: b.color || "#1f2933" }}>{b.name}</span>
              ) : null; })()}
            </div>
            <div className="tk-title" onClick={() => onOpen(t)}>{t.title || "(no title)"}</div>
            <div className="tk-meta">
              <span><b>{companyName(t.companyID)}</b></span>
              {t.contactName && <span>· {t.contactName}</span>}
              {t.hoursLogged > 0 && <span className="tk-hrs">⏱ {Math.round(t.hoursLogged * 4) / 4}h</span>}
            </div>
            <div className="tk-actions">
              <button onClick={() => onOpen(t)}>👁 View</button>
              <button className="go" onClick={() => onTime(t)}>⏱ Log Time</button>
            </div>
          </div>
        ))}
      </div>
    </>
  );
}

function CustomerScreen({ companies, companyName, statusBadge, selectedId, tickets, onSelectCustomer, onReload, onOpen, onTime }) {
  return (
    <>
      <div className="mob-top">
        <div style={{ flex: 1 }}>
          <h1>Customer View</h1>
          <div className="sub">Open tickets by customer</div>
        </div>
        {selectedId !== "" && (
          <button className="refresh" onClick={onReload} aria-label="Refresh" title="Refresh">↻</button>
        )}
      </div>

      <div className="cust-pick">
        <select value={selectedId} onChange={e => onSelectCustomer(e.target.value ? +e.target.value : "")}>
          <option value="">— Select a customer —</option>
          {companies.map(c => <option key={c.id} value={c.id}>{c.companyName || c.name}</option>)}
        </select>
      </div>

      <div className="mob-body">
        {selectedId === "" && <div className="empty">Pick a customer above to see their open tickets.</div>}
        {selectedId !== "" && tickets === null && (
          <div className="empty"><span className="spinner" /> Loading tickets…</div>
        )}
        {selectedId !== "" && tickets && tickets.length === 0 && (
          <div className="empty">No open tickets for {companyName(selectedId)}.</div>
        )}
        {selectedId !== "" && tickets && tickets.map(t => (
          <div className="tk" key={t.id}>
            <div className="tk-top" onClick={() => onOpen(t)}>
              <span className="tk-num">#{t.ticketNumber || t.id}</span>
              <span className={`tk-pri p${t.priority || 4}`}>{PRIORITY_LABELS[t.priority] || "—"}</span>
              {(() => { const b = statusBadge(t.status); return b.name ? (
                <span style={{ fontSize: 11, fontWeight: 700, color: b.color || "#1f2933" }}>{b.name}</span>
              ) : null; })()}
            </div>
            <div className="tk-title" onClick={() => onOpen(t)}>{t.title || "(no title)"}</div>
            <div className="tk-meta">
              {t.contactName && <span>{t.contactName}</span>}
              {t.assignedResourceName && <span>· assigned: {t.assignedResourceName}</span>}
              {t.hoursLogged > 0 && <span className="tk-hrs">⏱ {Math.round(t.hoursLogged * 4) / 4}h</span>}
            </div>
            <div className="tk-actions">
              <button onClick={() => onOpen(t)}>👁 View</button>
              <button className="go" onClick={() => onTime(t)}>⏱ Log Time</button>
            </div>
          </div>
        ))}
      </div>
    </>
  );
}

// ─── Ticket Detail: read-only info + notes + time entries, then Log Time ─────
function TicketDetailScreen({ ticket, companyName, atReference, timelog, onBack, onEdit, onViewNotes, onViewTime, onNotesLoaded, onEntriesLoaded }) {
  const [notes, setNotes] = useState(null);   // null = loading
  const [entries, setEntries] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [n, te] = await Promise.all([
          timelog.getTicketNotes ? timelog.getTicketNotes(ticket.id) : [],
          timelog.getTicketTime ? timelog.getTicketTime(ticket.id) : [],
        ]);
        if (!cancelled) {
          setNotes(n || []); setEntries(te || []);
          if (onNotesLoaded) onNotesLoaded(n || []);
          if (onEntriesLoaded) onEntriesLoaded(te || []);
        }
      } catch (e) {
        if (!cancelled) {
          setNotes([]); setEntries([]);
          if (onNotesLoaded) onNotesLoaded([]);
          if (onEntriesLoaded) onEntriesLoaded([]);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [ticket, timelog, onNotesLoaded, onEntriesLoaded]);

  const ref = atReference || {};

  // Resolve statuses/priorities/queues/resources: id|value → name|label|"First Last"
  const resolveRef = (list, id) => {
    if (!id && id !== 0) return "—";
    const item = (list || []).find(x => String(x.id ?? x.value) === String(id));
    if (!item) return `ID ${id}`;
    return item.name || item.label
      || [item.firstName, item.lastName].filter(Boolean).join(" ").trim()
      || `ID ${id}`;
  };

  // Resolve issue/sub-issue picklists: value → label
  const resolvePicklist = (list, val) => {
    if (!val) return "—";
    const item = (list || []).find(x => String(x.value) === String(val));
    return item?.label || String(val);
  };

  return (
    <>
      <div className="mob-top">
        <button className="back" onClick={onBack}>‹</button>
        <div style={{ flex: 1 }}>
          <h1>#{ticket.ticketNumber || ticket.id}</h1>
          <div className="sub">{companyName(ticket.companyID)}</div>
        </div>
      </div>

      <div className="mob-body">
        <div className="detail-info">
          <div style={{ fontSize: 15, fontWeight: 600, lineHeight: 1.35 }}>{ticket.title || "(no title)"}</div>
          {ticket.description && <div className="detail-desc">{ticket.description}</div>}
        </div>

        <div className="detail-fields">
          <div className="df-row">
            <span className="df-label">Status</span>
            <span className="df-val">{resolveRef(ref.statuses, ticket.status)}</span>
          </div>
          <div className="df-row">
            <span className="df-label">Priority</span>
            <span className="df-val">{resolveRef(ref.priorities, ticket.priority)}</span>
          </div>
          <div className="df-row">
            <span className="df-label">Queue</span>
            <span className="df-val">{resolveRef(ref.queues, ticket.queueID)}</span>
          </div>
          <div className="df-row">
            <span className="df-label">Company</span>
            <span className="df-val">{companyName(ticket.companyID)}</span>
          </div>
          <div className="df-row">
            <span className="df-label">Contact</span>
            <span className="df-val">{ticket.contactName || resolveRef(ref.contacts, ticket.contactID)}</span>
          </div>
          {ticket.contractID && (
            <div className="df-row">
              <span className="df-label">Contract</span>
              <span className="df-val">{resolveRef(ref.contracts, ticket.contractID)}</span>
            </div>
          )}
          {ticket.configurationItemID && (
            <div className="df-row">
              <span className="df-label">Asset</span>
              <span className="df-val">{String(ticket.configurationItemID)}</span>
            </div>
          )}
          {ticket.issueType && (
            <div className="df-row">
              <span className="df-label">Issue type</span>
              <span className="df-val">{resolvePicklist(ref.issueTypes, ticket.issueType)}</span>
            </div>
          )}
          {ticket.subIssueType && (
            <div className="df-row">
              <span className="df-label">Sub-issue</span>
              <span className="df-val">{resolvePicklist(ref.subIssueTypes, ticket.subIssueType)}</span>
            </div>
          )}
          {ticket.assignedResourceID && (
            <div className="df-row">
              <span className="df-label">Assigned to</span>
              <span className="df-val">{resolveRef(ref.resources, ticket.assignedResourceID)}</span>
            </div>
          )}
          {ticket.createDate && (
            <div className="df-row">
              <span className="df-label">Created</span>
              <span className="df-val">
                {new Date(ticket.createDate).toLocaleString(undefined,
                  { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}
              </span>
            </div>
          )}
        </div>
      </div>

      <div className="mob-foot" style={{ display: "flex", gap: 8 }}>
        <button className="btn-secondary" onClick={onEdit}>✎ Edit</button>
        <button className="btn-secondary" onClick={onViewNotes} disabled={notes === null}>
          💬 Notes{notes && notes.length > 0 ? ` (${notes.length})` : ""}
        </button>
        <button className="btn-secondary" onClick={onViewTime} disabled={entries === null}>
          ⏱ Time{entries && entries.length > 0 ? ` (${entries.length})` : ""}
        </button>
      </div>
    </>
  );
}

// ─── Ticket Notes: full-screen read-only notes list ─────────────────────────
function TicketNotesScreen({ ticket, notes, onBack }) {
  const fmt = (d) => d ? new Date(d).toLocaleString(undefined,
    { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : "";

  return (
    <>
      <div className="mob-top">
        <button className="back" onClick={onBack}>‹</button>
        <div style={{ flex: 1 }}>
          <h1>Notes</h1>
          <div className="sub">#{ticket.ticketNumber || ticket.id}</div>
        </div>
      </div>

      <div className="mob-body">
        {notes === null && <div className="empty"><span className="spinner" /> Loading notes…</div>}
        {notes && notes.length === 0 && <div className="empty">No notes on this ticket.</div>}
        {notes && notes.map(n => (
          <div className="note-card" key={n.id}>
            <div className="meta">
              <span>{n.createdByName || n.creatorResourceName || "—"}</span>
              <span>{fmt(n.createDateTime || n.lastActivityDate)}</span>
            </div>
            {n.title && <div style={{ fontWeight: 600, fontSize: 12, marginBottom: 3 }}>{n.title}</div>}
            <div className="body">{n.description || ""}</div>
          </div>
        ))}
      </div>
    </>
  );
}

// ─── Ticket Time Entries: full-screen read-only time list ───────────────────
function TicketTimeScreen({ ticket, entries, onBack }) {
  const fmt = (d) => d ? new Date(d).toLocaleString(undefined,
    { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : "";

  const total = (entries || []).reduce((sum, te) => sum + (te.hoursWorked || 0), 0);
  const rounded = Math.round(total * 4) / 4;

  return (
    <>
      <div className="mob-top">
        <button className="back" onClick={onBack}>‹</button>
        <div style={{ flex: 1 }}>
          <h1>Time Entries</h1>
          <div className="sub">
            #{ticket.ticketNumber || ticket.id}
            {entries && entries.length > 0 ? ` · ${rounded}h total` : ""}
          </div>
        </div>
      </div>

      <div className="mob-body">
        {entries === null && <div className="empty"><span className="spinner" /> Loading time entries…</div>}
        {entries && entries.length === 0 && <div className="empty">No time logged on this ticket.</div>}
        {entries && entries.map(te => (
          <div className="te-card" key={te.id}>
            <div className="meta">
              <span>{te.resourceName || "—"}</span>
              <span><span className="hrs">{te.hoursWorked}h</span>{" · "}{fmt(te.dateWorked)}</span>
            </div>
            {te.summaryNotes && <div className="body">{te.summaryNotes}</div>}
          </div>
        ))}
      </div>
    </>
  );
}

// ─── Screen 2: ticket edit / create ──────────────────────────────────────────
function TicketScreen({
  existing, atReference, companies, prefill, onGetContacts, onGetContracts, onGetAssets,
  onCreateTicket, updateTicket, onAddTicketNote,
  getSecondaryResources, addSecondaryResource, onBack, onSaved, showToast,
}) {
  const isNew = !existing;
  const ref = atReference || {};
  // The primary assignee on this mobile form isn't user-selectable: on create the
  // logged-in tech is assigned server-side (assignToMe); on edit it's the ticket's
  // current assignee. Use that to keep it out of the secondary dropdown.
  const primaryResourceId = existing?.assignedResourceID ?? "";
  const [saving, setSaving] = useState(false);
  const [contacts, setContacts] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [assets, setAssets] = useState([]);
  const [loadingCo, setLoadingCo] = useState(false);
  // True when a company is selected but its data came back empty and may still
  // be syncing — drives the "Loading customer data…" hint + auto-retry.
  const [dataPending, setDataPending] = useState(false);
  // Completion modal (shown when changing an existing ticket's status to a
  // Complete state from inside the ticket). Reason is optional, saved as a note.
  const COMPLETE_STATUS_IDS = [5, 32];
  const [showComplete, setShowComplete] = useState(false);
  const [completeReason, setCompleteReason] = useState("");

  // Field model mirrors MspOsApp's atTicket shape exactly.
  const [t, setT] = useState(() => ({
    title: existing?.title || prefill?.prefillTitle || "",
    description: existing?.description || prefill?.prefillDescription || "",
    priority: existing?.priority || 3,
    statusId: existing?.status || 1,
    // String, to match the <select> value type now that + coercion is gone.
    queueId: existing?.queueID ? String(existing.queueID) : "",
    issueType: existing?.issueType || "",
    subIssueType: existing?.subIssueType || "",
    companyId: existing?.companyID || "",
    contactId: existing?.contactID || "",
    configurationItemId: "",
    workTypeId: "",
    contractId: existing?.contractID || "",
    source: "",
    secondaryResourceId: "",
  }));
  const set = (patch) => setT(prev => ({ ...prev, ...patch }));

  // On edit, pre-fill the current secondary resource (first one, if any).
  useEffect(() => {
    if (!existing || !getSecondaryResources) return;
    getSecondaryResources(existing.id).then(items => {
      if (items.length > 0) set({ secondaryResourceId: items[0].resourceID });
    }).catch(() => {});
  }, [existing?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // load company-dependent dropdowns when company changes
  const loadCompanyData = useCallback(async (companyId, forceDefaultContract = false, attempt = 0) => {
    if (!companyId) { setContacts([]); setContracts([]); setAssets([]); setDataPending(false); return; }
    setLoadingCo(true);
    try {
      const [cts, ctr, ast] = await Promise.all([
        onGetContacts ? onGetContacts(companyId) : [],
        onGetContracts ? onGetContracts(companyId) : [],
        onGetAssets ? onGetAssets(companyId) : [],
      ]);
      const contactsArr = cts || [], contractsArr = ctr || [], assetsArr = ast || [];
      setContacts(contactsArr); setContracts(contractsArr); setAssets(assetsArr);

      // "Empty because the Autotask cache isn't warm yet" vs "genuinely empty":
      // a company with zero CONTACTS almost always means not-synced (every
      // customer has at least one contact), so use that as the retry signal.
      // Zero contracts/assets alone is legitimate and does NOT trigger a retry.
      const looksUnsynced = contactsArr.length === 0;
      if (looksUnsynced && attempt < 3) {
        setDataPending(true);
        setTimeout(() => loadCompanyData(companyId, forceDefaultContract, attempt + 1), 1500);
        return; // keep loadingCo/dataPending until a retry resolves or attempts run out
      }
      setDataPending(false);

      // Pre-select the customer's DEFAULT contract (isDefaultContract === true),
      // not just the first. forceDefaultContract is set when the company changes,
      // so we don't depend on a stale t.contractId from this closure.
      if (contractsArr.length) {
        setT(prev => {
          if (!forceDefaultContract && prev.contractId) return prev; // keep existing choice
          const def = contractsArr.find(c => c.isDefaultContract === true || c.isDefaultContract === 1);
          return { ...prev, contractId: (def || contractsArr[0]).id };
        });
      }
    } catch (e) {
      console.error("[timelog] loadCompanyData failed:", e?.response?.data?.error || e?.message || e);
      setDataPending(false);
    }
    setLoadingCo(false);
  }, [onGetContacts, onGetContracts, onGetAssets]); // eslint-disable-line

  useEffect(() => { if (t.companyId) loadCompanyData(t.companyId); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const subIssues = (ref.subIssueTypes || []).filter(
    s => s.isActive !== false && String(s.parentValue) === String(t.issueType)
  );

  const canSave = t.title && t.companyId !== "" && t.queueId !== "" && !saving;

  // The actual edit PATCH. If a closing reason is supplied, add it as a note
  // Add the selected secondary tech AFTER the ticket is created/updated. Secondary
  // resources are a separate AT sub-entity, so this is a follow-up call. Non-fatal:
  // the ticket is already saved, so a failure only warns. AT rejects a duplicate
  // ("already assigned as secondary") — treat that specific case as a silent no-op.
  const addSecondaryIfSelected = async (ticketId) => {
    if (!t.secondaryResourceId || !addSecondaryResource) return;
    try {
      await addSecondaryResource(ticketId, t.secondaryResourceId);
    } catch (e) {
      const msg = errText(e).toLowerCase();
      if (msg.includes("already")) return; // already assigned as secondary — no-op
      showToast("Ticket saved but secondary tech failed: " + errText(e), "err");
    }
  };

  // first (best-effort), then PATCH the ticket fields including the new status.
  const doEditSave = async (closingReason) => {
    if (closingReason && closingReason.trim() && onAddTicketNote) {
      try {
        await onAddTicketNote(existing.id, {
          title: "Reason for completion",
          description: closingReason.trim(),
          publish: 1,      // internal
          noteType: 1,     // standard task note
        });
      } catch (e) { /* non-fatal: still close the ticket */ }
    }
    await updateTicket(existing.id, {
      title: t.title, description: t.description,
      priority: t.priority, status: t.statusId,
      // Omit entirely when no queue is chosen — sending 0 or "" makes Autotask
      // apply its own default (Administration) rather than leaving the queue alone.
      ...(t.queueId ? { queueID: Number(t.queueId) } : {}),
      issueType: t.issueType || undefined, subIssueType: t.subIssueType || undefined,
      companyID: t.companyId, contactID: t.contactId || undefined,
      contractID: t.contractId || undefined,
      configurationItemID: t.configurationItemId || undefined,
      source: t.source || undefined,
    });
    await addSecondaryIfSelected(existing.id);
    onSaved(null, false);
  };

  const save = async () => {
    // Completing an existing ticket from inside it → prompt for a reason first.
    const becomingComplete = !isNew &&
      COMPLETE_STATUS_IDS.map(String).includes(String(t.statusId)) &&
      String(t.statusId) !== String(existing?.status);
    if (becomingComplete) { setShowComplete(true); return; }

    setSaving(true);
    try {
      if (isNew) {
        // reuse the SAME create handler MspOsApp uses (role auto-resolution etc.)
        // queueId is pulled out of the spread so an unchosen queue is ABSENT
        // rather than "" — autotaskService maps queueId straight to queueID, and
        // an empty value there makes AT fall back to Administration.
        const { queueId: chosenQueue, ...rest } = t;
        const result = await onCreateTicket({
          ...rest,
          ...(chosenQueue ? { queueId: Number(chosenQueue) } : {}),
          customFields: [],
        });
        const id = result?.ticket?.id || result?.ticket?.itemId;
        if (!id) { showToast("Create failed — check fields", "err"); setSaving(false); return; }
        // Make the logged-in tech the primary resource. Done via the timelog
        // PATCH (assignToMe) so the resource ID stays server-side (resolved from
        // the token) and the assignedResourceRoleID pairing is handled there.
        try {
          await updateTicket(id, { assignToMe: true });
        } catch (e) { /* non-fatal: ticket still created, just unassigned */ }
        // Add the secondary tech (separate AT sub-entity) once the ticket exists.
        await addSecondaryIfSelected(id);
        // Resolve the selected contact's name so the time screen can pre-fill "Who"
        const sel = contacts.find(c => String(c.id) === String(t.contactId));
        const contactName = sel ? [sel.firstName, sel.lastName].filter(Boolean).join(" ").trim() : null;
        onSaved({ id, ticketNumber: result?.ticket?.ticketNumber, title: t.title,
                  description: t.description, companyID: t.companyId, priority: t.priority,
                  status: t.statusId, contactID: t.contactId || null, contactName }, true);
      } else {
        await doEditSave(null);
      }
    } catch (e) {
      showToast("Save failed: " + errText(e), "err");
      setSaving(false);
    }
  };

  // Called from the completion modal's confirm button.
  const confirmComplete = async () => {
    setShowComplete(false);
    setSaving(true);
    try {
      await doEditSave(completeReason);
    } catch (e) {
      showToast("Save failed: " + errText(e), "err");
      setSaving(false);
    }
  };

  return (
    <>
      <div className="mob-top">
        <button className="back" onClick={onBack}>‹</button>
        <div style={{ flex: 1 }}>
          <h1>{isNew ? "New Ticket" : `Edit #${existing.ticketNumber || existing.id}`}</h1>
          <div className="sub">{isNew ? "Create a ticket, then log time" : "Update any field"}</div>
        </div>
      </div>

      <div className="mob-body">
        <div className="f">
          <label>Title <span className="req">*</span></label>
          <input value={t.title} onChange={e => set({ title: e.target.value })} placeholder="Ticket title…" />
        </div>

        <div className="f">
          <label>Description</label>
          <textarea value={t.description} onChange={e => set({ description: e.target.value })} placeholder="Describe the issue…" />
        </div>

        <div className="f-row">
          <div className="f">
            <label>Priority</label>
            <select value={t.priority} onChange={e => set({ priority: +e.target.value })}>
              {(ref.priorities || [{ id: 1, name: "Critical" }, { id: 2, name: "High" }, { id: 3, name: "Medium" }, { id: 4, name: "Low" }])
                .map(p => <option key={p.id || p.value} value={p.id || p.value}>{p.name || p.label}</option>)}
            </select>
          </div>
          <div className="f">
            <label>Status</label>
            <select value={t.statusId} onChange={e => set({ statusId: +e.target.value })}>
              {(ref.statuses || []).map(s => <option key={s.id || s.value} value={s.id || s.value}>{s.name || s.label}</option>)}
            </select>
          </div>
        </div>

        <div className="f">
          <label>Queue</label>
          {/* Kept as a STRING, not +e.target.value. The unary + turned the
              "— Select queue —" empty value into 0, which Autotask reads as "no
              queue given" and silently files the ticket under its own default
              (Administration). "" is now preserved and the payload omits the
              field entirely — see save/doEditSave. */}
          <select value={t.queueId} onChange={e => set({ queueId: e.target.value })}>
            <option value="">— Select queue —</option>
            {(ref.queues || []).filter(q => q.isActive !== false && q.isSystem !== true)
              .map(q => <option key={q.id || q.value} value={q.id || q.value}>{q.name || q.label}</option>)}
          </select>
          {!t.queueId && (
            <div className="hint" style={{ color: "var(--warn)" }}>
              Queue is required to save.
            </div>
          )}
        </div>

        <div className="f">
          <label>Secondary tech (optional)</label>
          <select value={t.secondaryResourceId} onChange={e => set({ secondaryResourceId: e.target.value })}>
            <option value="">— None —</option>
            {(ref.resources || [])
              .filter(r => r.isActive !== false &&
                // Cannot be the same as the primary assigned resource
                String(r.id) !== String(primaryResourceId))
              .map(r => {
                const name = r.name
                  || [r.firstName, r.lastName].filter(Boolean).join(" ").trim()
                  || `Resource ${r.id}`;
                return <option key={r.id} value={r.id}>{name}</option>;
              })}
          </select>
          {t.secondaryResourceId && String(t.secondaryResourceId) === String(primaryResourceId) && (
            <div className="hint" style={{ color: "var(--warn)" }}>
              Secondary cannot be the same as the primary tech.
            </div>
          )}
        </div>

        <div className="f-row">
          <div className="f">
            <label>Issue Type</label>
            <select value={t.issueType} onChange={e => set({ issueType: e.target.value, subIssueType: "" })}>
              <option value="">— Select —</option>
              {(ref.issueTypes || []).filter(i => i.isActive !== false)
                .map(i => <option key={i.value} value={i.value}>{i.label}</option>)}
            </select>
          </div>
          <div className="f">
            <label>Sub-Issue</label>
            <select value={t.subIssueType} onChange={e => set({ subIssueType: e.target.value })} disabled={!t.issueType}>
              <option value="">{t.issueType ? "— Select —" : "— Issue first —"}</option>
              {subIssues.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
            </select>
          </div>
        </div>

        <div className="f">
          <label>Company <span className="req">*</span></label>
          <select value={t.companyId} onChange={e => {
            const cid = e.target.value ? +e.target.value : "";
            set({ companyId: cid, contactId: "", contractId: "", configurationItemId: "" });
            loadCompanyData(cid, true); // force re-select of the new company's default contract
          }}>
            <option value="">— Select company —</option>
            {/* Pin Datatech to the top so it's always easy to find */}
            {companies
              .filter(c => (c.companyName || c.name || "").toLowerCase().includes("datatech"))
              .map(c => <option key={c.id} value={c.id}>{c.companyName || c.name}</option>)}
            {companies.some(c => (c.companyName || c.name || "").toLowerCase().includes("datatech")) && (
              <option disabled>──────────</option>
            )}
            {/* Remaining companies (Datatech excluded above to avoid a duplicate) */}
            {companies
              .filter(c => !(c.companyName || c.name || "").toLowerCase().includes("datatech"))
              .map(c => <option key={c.id} value={c.id}>{c.companyName || c.name}</option>)}
          </select>
          {dataPending && (
            <div className="hint" style={{ color: "var(--warn)" }}>
              <span className="spinner" /> Loading customer data from Autotask… one moment.
            </div>
          )}
        </div>

        <div className="f">
          <label>Contact</label>
          <select value={t.contactId} onChange={e => set({ contactId: +e.target.value })} disabled={loadingCo || dataPending || t.companyId === ""}>
            <option value="">{(loadingCo || dataPending) ? "Loading…" : t.companyId !== "" ? (contacts.length ? "— Select contact —" : "— No contacts —") : "— Company first —"}</option>
            {contacts.map(c => <option key={c.id} value={c.id}>{c.firstName} {c.lastName}</option>)}
          </select>
        </div>

        <div className="f">
          <label>Contract</label>
          <select value={t.contractId} onChange={e => set({ contractId: +e.target.value })} disabled={loadingCo || dataPending || t.companyId === ""}>
            <option value="">{(loadingCo || dataPending) ? "Loading…" : t.companyId !== "" ? (contracts.length ? "— Select contract —" : "— No contracts —") : "— Company first —"}</option>
            {contracts.map(c => <option key={c.id} value={c.id}>{c.contractName || c.name || `Contract ${c.id}`}</option>)}
          </select>
        </div>

        <div className="f">
          <label>Asset (optional)</label>
          <select value={t.configurationItemId} onChange={e => set({ configurationItemId: e.target.value ? +e.target.value : "" })} disabled={loadingCo || dataPending || t.companyId === ""}>
            <option value="">{(loadingCo || dataPending) ? "Loading…" : t.companyId !== "" ? (assets.length ? "— No asset —" : "— No assets —") : "— Company first —"}</option>
            {assets.map(a => <option key={a.id} value={a.id}>{a.referenceTitle || a.name || `Asset ${a.id}`}</option>)}
          </select>
        </div>
      </div>

      <div className="mob-foot">
        <button className="btn-primary" disabled={!canSave} onClick={save}>
          {saving ? <><span className="spinner" /> Saving…</> : isNew ? "Create ticket →" : "Save changes"}
        </button>
      </div>

      {showComplete && (
        <div className="modal-back" onClick={() => setShowComplete(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <h3>Complete ticket #{existing?.ticketNumber || existing?.id}</h3>
            <p>Add a reason for completing this ticket. It’ll be saved as a note. Optional — you can skip it.</p>
            <textarea value={completeReason} onChange={e => setCompleteReason(e.target.value)}
              placeholder="e.g. Resolved remotely — reset the service and confirmed with the user." autoFocus />
            <div className="modal-actions">
              <button className="cancel" onClick={() => setShowComplete(false)}>Cancel</button>
              <button className="confirm" onClick={confirmComplete}>Complete ticket</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// ─── Screen 3: time entry (Who / Issue / Resolution) ─────────────────────────
// NEW status (1) blocks time entries in Autotask, so a status dropdown lets the
// tech move the ticket off New before logging. Status options come from
// atReference (instance-specific IDs); we never hardcode an "In Progress" ID.
const NEW_STATUS_ID = 1;          // Autotask system default for New
const TIME_COMPLETE_IDS = [5, 32]; // don't offer "complete" states when logging time
const DEFAULT_WORK_TYPE_NAME = "Remote Support"; // pre-selected by NAME (id is instance-specific)

function TimeScreen({ ticket, companyName, atReference, suggestIssueSummary, createTimeEntry, updateTicket, onBack, onLogged, showToast }) {
  const [quick, setQuick] = useState(null);
  const [customHours, setCustomHours] = useState("");
  const [who, setWho] = useState(ticket.contactName || "");
  const [issue, setIssue] = useState("");
  const [resolution, setResolution] = useState("");
  const [aiLoading, setAiLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Status: default to the ticket's current status, UNLESS it's New (1) — then
  // leave unselected so the tech must pick a working status before logging.
  const currentStatus = ticket.status || ticket.statusId || NEW_STATUS_ID;
  const startsNew = String(currentStatus) === String(NEW_STATUS_ID);
  const [statusId, setStatusId] = useState(startsNew ? "" : currentStatus);

  // Selectable statuses: active, excluding only New (can't log time against New).
  // Complete states ARE included now, so you can close a ticket when logging time.
  const statusOptions = (atReference?.statuses || []).filter(s => {
    const id = s.id ?? s.value;
    return s.isActive !== false && String(id) !== String(NEW_STATUS_ID);
  });
  // Is the chosen status a "complete/close" state? If so, log time FIRST, then
  // close — closing first can block the time entry.
  const isCompleteStatus = (id) => TIME_COMPLETE_IDS.map(String).includes(String(id));

  // Work types (billing codes): default to "Remote Support" by name.
  const workTypeOptions = (atReference?.workTypes || []).filter(w => w.isActive !== false);
  const defaultWorkTypeId = (() => {
    const m = workTypeOptions.find(
      w => (w.name || w.label || "").trim().toLowerCase() === DEFAULT_WORK_TYPE_NAME.toLowerCase()
    );
    return m ? (m.id ?? m.value) : "";
  })();
  const [workTypeId, setWorkTypeId] = useState(defaultWorkTypeId);
  // If reference data arrives after first render, apply the default once.
  useEffect(() => {
    if (workTypeId === "" && defaultWorkTypeId !== "") setWorkTypeId(defaultWorkTypeId);
  }, [defaultWorkTypeId]); // eslint-disable-line

  // Start date/time — required when the contract uses start/stop time reporting.
  // Defaults to "now" in the tech's LOCAL time, formatted for <input datetime-local>.
  // End time is derived from start + the chosen hours; both are converted to UTC
  // ISO on submit (Autotask stores UTC).
  const nowLocalInput = () => {
    const d = new Date();
    d.setMinutes(d.getMinutes() - d.getTimezoneOffset()); // shift so toISOString gives local wall-clock
    return d.toISOString().slice(0, 16); // "YYYY-MM-DDTHH:mm"
  };
  const [startLocal, setStartLocal] = useState(nowLocalInput());

  // AI pre-fill the Issue field on open
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!suggestIssueSummary) return;
      setAiLoading(true);
      try {
        const s = await suggestIssueSummary(ticket.title || "", ticket.description || "");
        if (!cancelled && s) setIssue(s);
      } catch (e) { /* non-fatal */ }
      if (!cancelled) setAiLoading(false);
    })();
    return () => { cancelled = true; };
  }, [ticket, suggestIssueSummary]);

  const reSuggest = async () => {
    if (!suggestIssueSummary) return;
    setAiLoading(true);
    try {
      const s = await suggestIssueSummary(ticket.title || "", ticket.description || "");
      if (s) setIssue(s);
    } catch (e) { /* non-fatal */ }
    setAiLoading(false);
  };

  const hours = () => {
    const c = parseFloat(customHours);
    if (!isNaN(c) && c > 0) return c;
    return quick;
  };
  // Must pick a status if the ticket is still New (can't log time against New).
  const statusOk = !startsNew || (statusId !== "" && String(statusId) !== String(NEW_STATUS_ID));
  const workTypeOk = workTypeId !== "";
  const canLog = hours() > 0 && (who.trim() || issue.trim() || resolution.trim()) && statusOk && workTypeOk && !submitting;

  const buildSummary = () => {
    const parts = [];
    if (who.trim()) parts.push(`Who: ${who.trim()}`);
    if (issue.trim()) parts.push(`Issue: ${issue.trim()}`);
    if (resolution.trim()) parts.push(`Resolution: ${resolution.trim()}`);
    return parts.join("\n");
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      const statusChanged = statusId !== "" && String(statusId) !== String(currentStatus);
      const closing = statusChanged && isCompleteStatus(statusId);

      // If moving to a WORKING status (e.g. New → In Progress), PATCH FIRST —
      // Autotask rejects time entries against a New ticket. But if CLOSING the
      // ticket, do that AFTER the time entry (a closed ticket can block logging).
      if (statusChanged && !closing) {
        await updateTicket(ticket.id, { status: Number(statusId) });
      }

      // Create the time entry (work type + start/end times for contracts that
      // require start/stop reporting). startLocal is local wall-clock; convert
      // to UTC, end = start + chosen hours.
      const startUtc = new Date(startLocal);
      const endUtc = new Date(startUtc.getTime() + hours() * 3600 * 1000);
      await createTimeEntry({
        ticketID: ticket.id,
        hoursWorked: hours(),
        summaryNotes: buildSummary(),
        billingCodeID: Number(workTypeId),
        startDateTime: startUtc.toISOString().slice(0, 19),
        endDateTime: endUtc.toISOString().slice(0, 19),
        // The tech's LOCAL calendar date, taken straight off the picker.
        // Deliberately not derived from startDateTime on the server: that value
        // is UTC, and Perth is UTC+8, so anything started before 08:00 local
        // falls on the previous UTC day and would be dated a day early.
        dateWorked: String(startLocal).slice(0, 10),
      });

      // Now close, if that's what was chosen.
      if (closing) {
        await updateTicket(ticket.id, { status: Number(statusId) });
      }
      onLogged();
    } catch (e) {
      showToast("Failed: " + errText(e), "err");
      setSubmitting(false);
    }
  };

  return (
    <>
      <div className="mob-top">
        <button className="back" onClick={onBack}>‹</button>
        <div style={{ flex: 1 }}>
          <h1>Log Time</h1>
          <div className="sub">#{ticket.ticketNumber || ticket.id} · {companyName(ticket.companyID)}</div>
        </div>
      </div>

      <div className="mob-body">
        <div className="f">
          <label>Hours</label>
          <div className="hours">
            {QUICK_HOURS.map(h => (
              <button key={h} className={`chip ${quick === h && !customHours ? "sel" : ""}`}
                onClick={() => { setQuick(h); setCustomHours(""); }}>{h}h</button>
            ))}
          </div>
          <input type="number" inputMode="decimal" step="0.25" min="0" placeholder="Custom (e.g. 1.75)"
            value={customHours} onChange={e => { setCustomHours(e.target.value); setQuick(null); }} />
        </div>

        <div className="f">
          <label>Start date &amp; time</label>
          <input type="datetime-local" value={startLocal} onChange={e => setStartLocal(e.target.value)} />
          {hours() > 0 && startLocal && (
            <div className="hint">
              Ends {new Date(new Date(startLocal).getTime() + hours() * 3600 * 1000)
                .toLocaleString(undefined, { weekday: "short", hour: "2-digit", minute: "2-digit" })}
              {" "}({hours()}h later)
            </div>
          )}
        </div>

        <div className="f">
          <label>
            Status{startsNew && <span className="req"> — required, ticket is New</span>}
          </label>
          <select value={statusId} onChange={e => setStatusId(e.target.value ? +e.target.value : "")}>
            <option value="">{startsNew ? "— Pick a status to log time —" : "— Keep current status —"}</option>
            {statusOptions.map(s => {
              const id = s.id ?? s.value;
              return <option key={id} value={id}>{s.name || s.label}</option>;
            })}
          </select>
          {startsNew && (
            <div className="hint" style={{ color: "var(--warn)" }}>
              Autotask won't accept time against a New ticket — choose e.g. In Progress.
            </div>
          )}
          {!startsNew && statusId !== "" && String(statusId) !== String(currentStatus) && !isCompleteStatus(statusId) && (
            <div className="hint">Ticket status will change when you log time.</div>
          )}
          {statusId !== "" && isCompleteStatus(statusId) && (
            <div className="hint" style={{ color: "var(--warn)" }}>
              Ticket will be closed after the time entry is logged.
            </div>
          )}
        </div>

        <div className="f">
          <label>Work Type <span className="req">*</span></label>
          <select value={workTypeId} onChange={e => setWorkTypeId(e.target.value ? +e.target.value : "")}>
            <option value="">— Select work type —</option>
            {workTypeOptions.map(w => {
              const id = w.id ?? w.value;
              return <option key={id} value={id}>{w.name || w.label}</option>;
            })}
          </select>
          {workTypeId === "" && (
            <div className="hint" style={{ color: "var(--warn)" }}>Required for time entries.</div>
          )}
        </div>

        <div className="f">
          <label>Who — who requested the work</label>
          <input value={who} onChange={e => setWho(e.target.value)} placeholder="e.g. John Smith" />
        </div>

        <div className="f">
          <label>
            Issue — what was wrong
            <span className={`ai-chip ${aiLoading ? "spin" : ""}`} onClick={reSuggest}>
              {aiLoading ? <><span className="spinner" /> thinking</> : "✨ AI re-suggest"}
            </span>
          </label>
          <textarea value={issue} onChange={e => setIssue(e.target.value)}
            placeholder={aiLoading ? "Generating from ticket…" : "Pre-filled from the ticket — edit as needed"} />
          <div className="hint">Pre-filled from the ticket. Edit freely.</div>
        </div>

        <div className="f">
          <label>Resolution — what you did to fix it</label>
          <textarea value={resolution} onChange={e => setResolution(e.target.value)}
            placeholder="e.g. Repaired Office, deleted OST, Outlook rebuilt it — resolved." />
        </div>

        <div className="f" style={{ opacity: .7 }}>
          <label>Preview — combined note sent to Autotask</label>
          <div style={{ whiteSpace: "pre-line", fontSize: 13, background: "#fff", border: "1px solid #d6dde2",
            borderRadius: 11, padding: 13, minHeight: 50, lineHeight: 1.55 }}>
            {buildSummary() || <span style={{ color: "#9aa5ad" }}>Fill in the fields above…</span>}
          </div>
        </div>
      </div>

      <div className="mob-foot">
        <button className="btn-primary" disabled={!canLog} onClick={submit}>
          {submitting
            ? <><span className="spinner" /> {isCompleteStatus(statusId) ? "Logging & closing…" : "Logging…"}</>
            : isCompleteStatus(statusId)
              ? `Log ${hours() || ""}${hours() ? "h" : ""} & close ticket`
              : `Log ${hours() || ""}${hours() ? "h" : "time"}`}
        </button>
      </div>
    </>
  );
}

// ═══ IT Glue section ═════════════════════════════════════════════════════════
// Self-contained third tab: pick a customer → resolve its IT Glue org (matched
// by Autotask company NAME) → Passwords | Documents | Configurations sub-tabs.
// The IT Glue API key never reaches the browser; revealed password values are
// held only transiently (blurred, auto-hidden after 20s, never persisted).

const itgCss = `
.itg-sub { flex-shrink:0; display:flex; border-top:1px solid var(--line); background:#fff;
  padding-bottom:env(safe-area-inset-bottom); }
.itg-sub button { flex:1; background:none; border:none; cursor:pointer; font-family:inherit;
  padding:10px 0 9px; font-size:11px; font-weight:600; color:var(--muted);
  display:flex; flex-direction:column; align-items:center; gap:3px; }
.itg-sub button.on { color:var(--teal); }
.itg-sub button .ti { font-size:18px; line-height:1; }
.itg-row { background:var(--card); border:1px solid var(--line); border-radius:12px;
  padding:12px 13px; margin-bottom:9px; }
.itg-row .nm { font-size:14px; font-weight:600; line-height:1.3; }
.itg-row .un { font-size:12px; color:var(--muted); margin-top:2px; }
.itg-pw { display:flex; align-items:center; gap:8px; margin-top:9px; }
.itg-pw .val { flex:1; font-family:ui-monospace,Menlo,monospace; font-size:14px;
  background:#f4f6f8; border:1px solid var(--line); border-radius:8px; padding:9px 11px;
  overflow:hidden; text-overflow:ellipsis; white-space:nowrap; min-width:0; }
.itg-pw .val.blur { filter:blur(6px); user-select:none; -webkit-user-select:none; }
.itg-pw button { border:1px solid var(--line); background:#f8fafb; color:var(--text);
  border-radius:8px; padding:9px 12px; font-size:12px; font-weight:600; cursor:pointer;
  font-family:inherit; flex-shrink:0; }
.itg-pw button.go { background:var(--teal); color:#fff; border-color:var(--teal); }
.itg-add { width:100%; border:1px dashed #c2ccd2; background:#fff; color:var(--teal);
  border-radius:12px; padding:13px; font-size:14px; font-weight:700; cursor:pointer;
  font-family:inherit; margin-bottom:10px; }
.itg-add:active { background:#f4f6f8; }
/* Full-screen IT Glue form: takes over the screen (covers both tab bars), so its
   pinned mob-foot buttons can't fall behind a tab bar and mob-body owns scroll. */
.itg-screen { position:fixed; inset:0; z-index:70; background:var(--bg);
  display:flex; flex-direction:column; padding-top:env(safe-area-inset-top); }
/* flex:1 + the footer's own display:flex already made these share the width
   evenly. What made them look cramped was the LABEL wrapping to two lines once
   a count appeared ("💬 Notes (12)"), so: nowrap, and a little less side padding
   to buy the text room. Shared with the modal Cancel buttons, which are
   unaffected by both. */
.btn-secondary { flex:1; background:#f4f6f8; color:var(--text); border:1px solid var(--line);
  border-radius:12px; padding:15px 6px; font-size:14px; font-weight:700; cursor:pointer;
  font-family:inherit; text-align:center; white-space:nowrap; }
.btn-secondary:active { background:#e8edf1; }
.pw-search { display:flex; align-items:center; gap:8px; margin-bottom:10px; }
.pw-search input { flex:1; min-width:0; background:#fff; border:1px solid #d6dde2; border-radius:11px;
  padding:11px 13px; font-size:16px; font-family:inherit; color:var(--text); outline:none; }
.pw-search input:focus { border-color:var(--teal); }
.pw-search .clr { background:#eef1f3; border:none; border-radius:8px; width:32px; height:32px;
  font-size:13px; cursor:pointer; color:var(--muted); flex-shrink:0; }
.pw-search .cnt { font-size:11px; color:var(--muted); font-weight:600; flex-shrink:0; }
.sec-head { font-size:11px; font-weight:700; color:var(--teal); text-transform:uppercase;
  letter-spacing:.05em; margin:16px 2px 8px; padding-top:8px; border-top:1px solid var(--line); }
.sec-head:first-child { border-top:none; padding-top:0; margin-top:4px; }
`;

// Read-only HTML escape for document content built from user text.
function escHtml(s) {
  return String(s || "").replace(/[&<>"']/g, c =>
    ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));
}
// Read a File as raw base64 (strips the data: URL prefix).
function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result).split(",")[1] || "");
    r.onerror = reject;
    r.readAsDataURL(file);
  });
}
// Downscale an image to fit maxPx on its longest edge and re-encode as JPEG,
// returning raw base64 (no data: prefix). A 4032×3024 iPhone photo (~4MB)
// comes out around 1200×900 / ~200KB — plenty for documentation, and it keeps
// a multi-photo batch well inside the 50mb body limit on a site connection.
//
// Browsers have applied EXIF orientation when decoding since Safari 13.1 /
// Chrome 81, so img.width/height are already the upright dimensions and the
// canvas copy comes out the right way up. No manual EXIF handling needed.
function resizeImageToBase64(file, maxPx = 1200, quality = 0.75) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(url);
      try {
        const scale = Math.min(1, maxPx / Math.max(img.width, img.height));
        const nw = Math.max(1, Math.round(img.width * scale));
        const nh = Math.max(1, Math.round(img.height * scale));
        const canvas = document.createElement("canvas");
        canvas.width = nw;
        canvas.height = nh;
        canvas.getContext("2d").drawImage(img, 0, 0, nw, nh);
        const dataUrl = canvas.toDataURL("image/jpeg", quality);
        const b64 = dataUrl.split(",")[1] || "";
        if (!b64) throw new Error("canvas produced no data");
        resolve(b64);
      } catch (e) { reject(e); }
    };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error("Image decode failed")); };
    img.src = url;
  });
}
// Approximate decoded byte count of a base64 string, for the staged-photo label.
function base64Kb(b64) {
  const padding = (b64.endsWith("==") ? 2 : b64.endsWith("=") ? 1 : 0);
  return Math.round(((b64.length * 3) / 4 - padding) / 1024);
}

function ITGlueScreen({ itglue, companies, companyName, initialCompanyId, onExit, showToast }) {
  const [companyId, setCompanyId] = useState(initialCompanyId || "");
  const [orgId, setOrgId] = useState(null);
  const [resolving, setResolving] = useState(false);
  const [orgError, setOrgError] = useState("");
  const [subTab, setSubTab] = useState("passwords");

  const resolveOrg = useCallback(async (cid) => {
    setOrgId(null); setOrgError("");
    if (!cid) return;
    setResolving(true);
    try {
      const id = await itglue.resolveOrg(companyName(cid));
      setOrgId(id);
    } catch (e) {
      setOrgError(errText(e));
    }
    setResolving(false);
  }, [itglue, companyName]);

  // Resolve on mount if we arrived with a customer already chosen (Customer View).
  useEffect(() => { if (companyId) resolveOrg(companyId); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const pickCompany = (cid) => { setCompanyId(cid); resolveOrg(cid); };

  return (
    <>
      <style>{itgCss}</style>
      <div className="mob-top">
        <button className="back" onClick={onExit} aria-label="Exit IT Glue">‹</button>
        <div style={{ flex: 1 }}>
          <h1>IT Glue</h1>
          <div className="sub">{orgId ? companyName(companyId) : "Pick a customer"}</div>
        </div>
      </div>

      <div className="cust-pick">
        <select value={companyId} onChange={e => pickCompany(e.target.value ? +e.target.value : "")}>
          <option value="">— Select a customer —</option>
          {companies.map(c => <option key={c.id} value={c.id}>{c.companyName || c.name}</option>)}
        </select>
      </div>

      {!orgId && (
        <div className="mob-body">
          {companyId === "" && <div className="empty">Pick a customer to open their IT Glue documentation.</div>}
          {companyId !== "" && resolving && (
            <div className="empty"><span className="spinner" /> Matching IT Glue organization…</div>
          )}
          {companyId !== "" && !resolving && orgError && (
            <div className="empty">
              Couldn’t match an IT Glue organization for<br /><b>{companyName(companyId)}</b>.
              <br /><br /><span style={{ color: "var(--danger)" }}>{orgError}</span>
            </div>
          )}
        </div>
      )}

      {orgId && subTab === "passwords" && <ITGPasswords itglue={itglue} orgId={orgId} showToast={showToast} />}
      {orgId && subTab === "documents" && <ITGDocuments itglue={itglue} orgId={orgId} showToast={showToast} />}
      {orgId && subTab === "configs"    && <ITGConfigs   itglue={itglue} orgId={orgId} showToast={showToast} />}

      {orgId && (
        <nav className="itg-sub">
          <button className={subTab === "passwords" ? "on" : ""} onClick={() => setSubTab("passwords")}>
            <span className="ti">🔑</span>Passwords
          </button>
          <button className={subTab === "documents" ? "on" : ""} onClick={() => setSubTab("documents")}>
            <span className="ti">📄</span>Documents
          </button>
          <button className={subTab === "configs" ? "on" : ""} onClick={() => setSubTab("configs")}>
            <span className="ti">🖥</span>Configs
          </button>
        </nav>
      )}
    </>
  );
}

// ─── IT Glue: Passwords ───────────────────────────────────────────────────────
function ITGPasswords({ itglue, orgId, showToast }) {
  const [data, setData] = useState(null);  // { passwords, folders } | null = loading
  const [creating, setCreating] = useState(false);
  const [q, setQ] = useState("");

  const load = useCallback(async () => {
    setData(null);
    try { setData(await itglue.listPasswords(orgId)); }
    catch (e) { setData({ passwords: [], folders: [] }); showToast("Couldn't load passwords: " + errText(e), "err"); }
  }, [itglue, orgId, showToast]);
  useEffect(() => { load(); }, [load]);

  const passwords = data?.passwords || [];
  const folders = data?.folders || [];
  // Client-side filter over the already-loaded list. The list carries names +
  // usernames only (never secret values), so search never touches a password.
  const t = q.trim().toLowerCase();
  const filtered = t
    ? passwords.filter(p =>
        (p.name || "").toLowerCase().includes(t) ||
        (p.username || "").toLowerCase().includes(t)) // username = email in IT Glue
    : passwords;

  return (
    <div className="mob-body">
      <button className="itg-add" onClick={() => setCreating(true)}>＋ New password</button>
      {data && passwords.length > 0 && (
        <div className="pw-search">
          <input value={q} onChange={e => setQ(e.target.value)} inputMode="search"
            autoCapitalize="none" autoCorrect="off" placeholder="Search name or email…" />
          {q && <button className="clr" onClick={() => setQ("")} aria-label="Clear">✕</button>}
          <span className="cnt">{filtered.length} of {passwords.length}</span>
        </div>
      )}
      {data === null && <div className="empty"><span className="spinner" /> Loading passwords…</div>}
      {data && passwords.length === 0 && <div className="empty">No passwords in this organization.</div>}
      {data && passwords.length > 0 && filtered.length === 0 && <div className="empty">No matches for “{q}”.</div>}
      {filtered.map(p => <ITGPasswordRow key={p.id} pw={p} itglue={itglue} showToast={showToast} />)}
      {creating && (
        <ITGNewPassword itglue={itglue} orgId={orgId} folders={folders} showToast={showToast}
          onClose={() => setCreating(false)} onCreated={() => { setCreating(false); load(); }} />
      )}
    </div>
  );
}

// One password row. The secret is fetched on demand, shown blurred, auto-hidden
// after 20s, and wiped from state on hide/unmount — never persisted.
function ITGPasswordRow({ pw, itglue, showToast }) {
  const [value, setValue] = useState(null);
  const [busy, setBusy] = useState(false);
  const timerRef = useRef(null);

  const clearReveal = useCallback(() => {
    setValue(null);
    if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; }
  }, []);
  // Wipe the secret on unmount AND when the app backgrounds (before the OS takes
  // an app-switcher snapshot) — visibilitychange fires on tab hide / app switch.
  useEffect(() => {
    const onHide = () => { if (document.visibilityState === "hidden") clearReveal(); };
    document.addEventListener("visibilitychange", onHide);
    return () => { document.removeEventListener("visibilitychange", onHide); clearReveal(); };
  }, [clearReveal]);

  const reveal = async () => {
    if (value) { clearReveal(); return; }   // toggle hide
    setBusy(true);
    try {
      const full = await itglue.revealPassword(pw.id);
      setValue(full?.password || "");
      timerRef.current = setTimeout(clearReveal, 15000); // short window — auto re-hide + wipe
    } catch (e) { showToast("Reveal failed: " + errText(e), "err"); }
    setBusy(false);
  };

  // Copy is a known clipboard leak path; keep it but auto-clear the clipboard
  // after 45s so a synced/synced-history clipboard doesn't retain the secret.
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value || "");
      showToast("Copied — clipboard clears in 45s", "ok");
      setTimeout(() => navigator.clipboard.writeText("").catch(() => {}), 45000);
    } catch (e) { showToast("Copy not available", "err"); }
  };

  return (
    <div className="itg-row">
      <div className="nm">{pw.name || "(unnamed)"}</div>
      {pw.username && <div className="un">{pw.username}</div>}
      <div className="itg-pw">
        <div className={`val ${value ? "" : "blur"}`}>{value || "••••••••••"}</div>
        <button className="go" onClick={reveal} disabled={busy}>
          {busy ? <span className="spinner" /> : value ? "Hide" : "Reveal"}
        </button>
        {value && <button onClick={copy}>Copy</button>}
      </div>
    </div>
  );
}

function ITGNewPassword({ itglue, orgId, folders, showToast, onClose, onCreated }) {
  const [f, setF] = useState({ name: "", username: "", password: "", url: "", notes: "", folderId: "" });
  const [saving, setSaving] = useState(false);
  const set = (patch) => setF(prev => ({ ...prev, ...patch }));

  const save = async () => {
    if (!f.name.trim() || !f.password) { showToast("Name and password are required", "err"); return; }
    setSaving(true);
    try {
      await itglue.createPassword(orgId, {
        name: f.name.trim(), username: f.username.trim() || undefined,
        password: f.password, url: f.url.trim() || undefined,
        notes: f.notes.trim() || undefined, folderId: f.folderId || undefined,
      });
      showToast("Password created", "ok");
      onCreated();
    } catch (e) { showToast("Create failed: " + errText(e), "err"); setSaving(false); }
  };

  return (
    <div className="itg-screen">
      <div className="mob-top">
        <button className="back" onClick={onClose}>‹</button>
        <div style={{ flex: 1 }}><h1>New password</h1></div>
      </div>
      <div className="mob-body">
        <div className="f"><label>Name <span className="req">*</span></label>
          <input value={f.name} onChange={e => set({ name: e.target.value })} placeholder="e.g. Firewall admin" /></div>
        <div className="f"><label>Username</label>
          <input value={f.username} onChange={e => set({ username: e.target.value })} autoCapitalize="none" /></div>
        <div className="f"><label>Password <span className="req">*</span></label>
          <input value={f.password} onChange={e => set({ password: e.target.value })} autoCapitalize="none" /></div>
        <div className="f"><label>URL</label>
          <input value={f.url} onChange={e => set({ url: e.target.value })} autoCapitalize="none" placeholder="https://…" /></div>
        {folders.length > 0 && (
          <div className="f"><label>Folder</label>
            <select value={f.folderId} onChange={e => set({ folderId: e.target.value })}>
              <option value="">— No folder —</option>
              {folders.map(fo => <option key={fo.id} value={fo.id}>{fo.name}</option>)}
            </select></div>
        )}
        <div className="f"><label>Notes</label>
          <textarea value={f.notes} onChange={e => set({ notes: e.target.value })} /></div>
      </div>
      <div className="mob-foot" style={{ display: "flex", gap: 10 }}>
        <button className="btn-secondary" onClick={onClose}>Cancel</button>
        <button className="btn-primary" style={{ flex: 1 }} onClick={save} disabled={saving}>{saving ? "Saving…" : "Create"}</button>
      </div>
    </div>
  );
}

// ─── IT Glue: Documents + site photos ─────────────────────────────────────────
function ITGDocuments({ itglue, orgId, showToast }) {
  const [folders, setFolders] = useState([]);
  const [mode, setMode] = useState(null); // "doc" | "photo" | null

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try { const fl = await itglue.docFolders(orgId); if (!cancelled) setFolders(fl || []); }
      catch (e) { /* non-fatal — folder picker just stays empty */ }
    })();
    return () => { cancelled = true; };
  }, [itglue, orgId]);

  return (
    <div className="mob-body">
      <button className="itg-add" onClick={() => setMode("doc")}>＋ New document</button>
      <button className="itg-add" onClick={() => setMode("photo")}>📷 Add site photo</button>
      <div className="empty">Create a document with a header and notes, or capture a site photo into a document folder.</div>
      {mode === "doc" && (
        <ITGNewDocument itglue={itglue} orgId={orgId} folders={folders} showToast={showToast} onClose={() => setMode(null)} />
      )}
      {mode === "photo" && (
        <ITGSitePhoto itglue={itglue} orgId={orgId} folders={folders} showToast={showToast} onClose={() => setMode(null)} />
      )}
    </div>
  );
}

function ITGNewDocument({ itglue, orgId, folders, showToast, onClose }) {
  const [name, setName] = useState("");
  const [body, setBody] = useState("");
  const [folderId, setFolderId] = useState("");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!name.trim()) { showToast("A document name is required", "err"); return; }
    setSaving(true);
    try {
      // Header = the document name; body paragraphs follow.
      const paras = body.split(/\n{2,}/).map(p => `<p>${escHtml(p).replace(/\n/g, "<br/>")}</p>`).join("");
      const contentHtml = `<h1>${escHtml(name.trim())}</h1>${paras}`;
      await itglue.createDoc(orgId, { name: name.trim(), contentHtml, folderId: folderId || undefined });
      showToast("Document created", "ok");
      onClose();
    } catch (e) { showToast("Create failed: " + errText(e), "err"); setSaving(false); }
  };

  return (
    <div className="itg-screen">
      <div className="mob-top">
        <button className="back" onClick={onClose}>‹</button>
        <div style={{ flex: 1 }}><h1>New document</h1></div>
      </div>
      <div className="mob-body">
        <div className="f"><label>Name / header <span className="req">*</span></label>
          <input value={name} onChange={e => setName(e.target.value)} placeholder="e.g. On-site visit notes" /></div>
        <div className="f"><label>Body</label>
          <textarea value={body} onChange={e => setBody(e.target.value)} style={{ minHeight: 130 }}
            placeholder="Write the document body…" /></div>
        {folders.length > 0 && (
          <div className="f"><label>Folder</label>
            <select value={folderId} onChange={e => setFolderId(e.target.value)}>
              <option value="">— No folder —</option>
              {folders.map(fo => <option key={fo.id} value={fo.id}>{fo.name}</option>)}
            </select></div>
        )}
      </div>
      <div className="mob-foot" style={{ display: "flex", gap: 10 }}>
        <button className="btn-secondary" onClick={onClose}>Cancel</button>
        <button className="btn-primary" style={{ flex: 1 }} onClick={save} disabled={saving}>{saving ? "Saving…" : "Create"}</button>
      </div>
    </div>
  );
}

function ITGSitePhoto({ itglue, orgId, folders, showToast, onClose }) {
  const [header, setHeader] = useState("");
  const [text, setText] = useState("");
  const [folderId, setFolderId] = useState("");
  // Staged photos: { id, base64 (prefix-stripped), srcName, url (preview), uploaded }
  const [photos, setPhotos] = useState([]);
  const [docId, setDocId] = useState(null); // created once; reused on retry
  const [saving, setSaving] = useState(false);
  const [progress, setProgress] = useState("");

  // Revoke preview object URLs on unmount (ref holds the latest list).
  const photosRef = useRef([]);
  photosRef.current = photos;
  useEffect(() => () => { photosRef.current.forEach(p => p.url && URL.revokeObjectURL(p.url)); }, []);

  // Append picked/captured files. `multiple` lets the tech select several at once;
  // reopening the picker (or camera) appends more. Each is downscaled to a JPEG
  // (raw base64, no data: prefix) for upload + an object URL for preview.
  //
  // If the canvas can't decode the file — an exotic format, or HEIC on a browser
  // that won't render it — fall back to sending the original bytes rather than
  // dropping the photo. A large upload beats a lost one.
  const onPick = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = ""; // allow re-picking the same file / retaking
    const added = [];
    const failed = [];
    for (const f of files) {
      let base64 = null;
      let srcName = f.name;
      try {
        base64 = await resizeImageToBase64(f);
        // The canvas always emits JPEG, so the name must say .jpg. The backend
        // derives the uploaded file's extension from srcName — leaving a .png
        // or .heic name on JPEG bytes mislabels the file in IT Glue.
        srcName = f.name.replace(/\.[a-z0-9]+$/i, "") + ".jpg";
      } catch (_e) {
        // Fallback sends the original bytes untouched, so keep the original name.
        try { base64 = await fileToBase64(f); } catch (_e2) { /* unreadable */ }
      }
      if (!base64) { failed.push(f.name); continue; }
      added.push({
        id: `${f.name}-${f.size}-${Math.random().toString(36).slice(2)}`,
        base64, srcName, url: URL.createObjectURL(f), uploaded: false,
        sizeKb: base64Kb(base64),
      });
    }
    if (added.length) setPhotos(prev => [...prev, ...added]);
    // Silently dropping a photo is how the last IT Glue bug stayed invisible —
    // say so instead.
    if (failed.length) {
      showToast(`Couldn't read ${failed.length === 1 ? failed[0] : `${failed.length} photos`}`, "err");
    }
  };

  const removePhoto = (id) => setPhotos(prev => {
    const p = prev.find(x => x.id === id);
    if (p?.url) URL.revokeObjectURL(p.url);
    return prev.filter(x => x.id !== id);
  });

  const pendingCount = photos.filter(p => !p.uploaded).length;

  const save = async () => {
    const pending = photos.filter(p => !p.uploaded);
    if (!pending.length) { onClose(); return; }
    setSaving(true);
    const title = header.trim() || "Site photo";
    setProgress(`Uploading ${pending.length} photo${pending.length === 1 ? "" : "s"}…`);

    // ONE call: the backend creates the document, uploads each photo, then
    // rewrites the document content to embed them — so the photos show up in
    // the document body rather than as attachments below it.
    let result;
    try {
      result = await itglue.createDocWithPhotos(orgId, {
        name: title,
        text: text.trim() || title,
        folderId: folderId || undefined,
        photos: pending.map(p => ({
          // fileToBase64 already strips the prefix; strip again defensively —
          // IT Glue 422s if a full data: URL reaches it.
          base64:  (p.base64 || "").replace(/^data:[^;]+;base64,/, ""),
          srcName: p.srcName || "photo.jpg",
        })),
      });
    } catch (e) {
      setProgress("");
      showToast("Upload failed: " + errText(e), "err");
      setSaving(false);
      return;
    }
    setProgress("");

    // result.failed holds 1-based indexes into the array we just sent.
    const failedIdx = new Set(result.failed || []);
    const okIds = pending.filter((_p, i) => !failedIdx.has(i + 1)).map(p => p.id);
    setPhotos(prev => prev.map(p => okIds.includes(p.id) ? { ...p, uploaded: true } : p));
    setDocId(result.docId || null);

    if (!result.uploaded) {
      showToast("Document created but no photos uploaded — check IT Glue", "err");
      setSaving(false);
    } else if (failedIdx.size) {
      showToast(`Created · ${result.uploaded}/${pending.length} photos — ${failedIdx.size} failed`, "err");
      setSaving(false);
    } else if (result.embedFailed) {
      showToast(`${result.uploaded} photo${result.uploaded === 1 ? "" : "s"} uploaded but not embedded — check IT Glue`, "err");
      onClose();
    } else {
      showToast(`Document created with ${result.uploaded} photo${result.uploaded === 1 ? "" : "s"} ✓`, "ok");
      onClose();
    }
  };

  // Retry can't top up the document already created — the endpoint creates one
  // per call — so say so plainly rather than implying it resumes the same doc.
  const btnLabel = saving
    ? (progress || "Uploading…")
    : docId ? `Retry ${pendingCount} in a new document`
            : `Upload ${pendingCount || ""} photo${pendingCount === 1 ? "" : "s"}`.replace("  ", " ").trim();

  return (
    <div className="itg-screen">
      <div className="mob-top">
        <button className="back" onClick={onClose}>‹</button>
        <div style={{ flex: 1 }}><h1>Add site photos</h1></div>
      </div>
      <div className="mob-body">
        <div className="f"><label>Header</label>
          <input value={header} onChange={e => setHeader(e.target.value)} placeholder="e.g. Server rack — Comms room" /></div>
        <div className="f"><label>Text</label>
          <textarea value={text} onChange={e => setText(e.target.value)}
            placeholder="Notes about these photos…" /></div>
        <div className="f"><label>Photos <span className="req">*</span></label>
          <input type="file" accept="image/*" capture="environment" multiple onChange={onPick} /></div>
        {photos.length > 0 && (
          <div className="f">
            <label>Staged ({photos.length})</label>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
              {photos.map(p => (
                <div key={p.id} style={{ position: "relative", width: 64, height: 64 }}>
                  <img src={p.url} alt="" style={{ width: 64, height: 64, objectFit: "cover",
                    borderRadius: 8, border: "1px solid var(--line)", opacity: p.uploaded ? 0.4 : 1 }} />
                  {p.uploaded && <span style={{ position: "absolute", inset: 0, display: "flex",
                    alignItems: "center", justifyContent: "center", color: "#fff", fontSize: 22,
                    textShadow: "0 1px 3px rgba(0,0,0,.6)" }}>✓</span>}
                  {!p.uploaded && !saving && (
                    <button onClick={() => removePhoto(p.id)} aria-label="Remove" style={{ position: "absolute",
                      top: -6, right: -6, width: 20, height: 20, borderRadius: "50%", border: "none",
                      background: "var(--danger)", color: "#fff", fontSize: 12, lineHeight: 1, cursor: "pointer" }}>✕</button>
                  )}
                  {!p.uploaded && p.sizeKb != null && (
                    <span style={{ position: "absolute", bottom: 0, left: 0, right: 0, textAlign: "center",
                      fontSize: 9, lineHeight: "12px", color: "#fff", background: "rgba(0,0,0,.55)",
                      borderRadius: "0 0 8px 8px" }}>{p.sizeKb}KB</span>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
        {folders.length > 0 && (
          <div className="f"><label>Folder</label>
            <select value={folderId} onChange={e => setFolderId(e.target.value)}>
              <option value="">— No folder —</option>
              {folders.map(fo => <option key={fo.id} value={fo.id}>{fo.name}</option>)}
            </select></div>
        )}
      </div>
      <div className="mob-foot" style={{ display: "flex", gap: 10 }}>
        <button className="btn-secondary" onClick={onClose}>Cancel</button>
        <button className="btn-primary" style={{ flex: 1 }} onClick={save} disabled={saving || pendingCount === 0}>{btnLabel}</button>
      </div>
    </div>
  );
}

// ─── IT Glue: Configurations ──────────────────────────────────────────────────
function ITGConfigs({ itglue, orgId, showToast }) {
  const [list, setList] = useState(null);
  const [ref0, setRef0] = useState({ types: [], statuses: [] });
  const [editing, setEditing] = useState(undefined); // undefined=none, null=new, obj=edit

  const load = useCallback(async () => {
    setList(null);
    try { setList(await itglue.listConfigs(orgId)); }
    catch (e) { setList([]); showToast("Couldn't load configurations: " + errText(e), "err"); }
  }, [itglue, orgId, showToast]);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try { const r = await itglue.configRef(); if (!cancelled) setRef0(r || { types: [], statuses: [] }); }
      catch (e) { /* non-fatal — form selects just stay empty */ }
    })();
    return () => { cancelled = true; };
  }, [itglue]);

  return (
    <div className="mob-body">
      <button className="itg-add" onClick={() => setEditing(null)}>＋ New configuration</button>
      {list === null && <div className="empty"><span className="spinner" /> Loading configurations…</div>}
      {list && list.length === 0 && <div className="empty">No configurations in this organization.</div>}
      {list && list.map(c => (
        <div className="itg-row" key={c.id} style={{ cursor: "pointer" }} onClick={() => setEditing(c)}>
          <div className="nm">{c.name || "(unnamed)"}</div>
          <div className="un">
            {c["configuration-type-name"] || c.configurationTypeName || ""}
            {(c["configuration-status-name"] || c.configurationStatusName)
              ? " · " + (c["configuration-status-name"] || c.configurationStatusName) : ""}
            {c["primary-ip"] ? " · " + c["primary-ip"] : ""}
          </div>
        </div>
      ))}
      {editing !== undefined && (
        <ITGConfigForm itglue={itglue} orgId={orgId} existing={editing} ref0={ref0} showToast={showToast}
          onClose={() => setEditing(undefined)} onSaved={() => { setEditing(undefined); load(); }} />
      )}
    </div>
  );
}

function ITGConfigForm({ itglue, orgId, existing, ref0, showToast, onClose, onSaved }) {
  const isNew = !existing;
  const d = (v) => (v ? String(v).slice(0, 10) : ""); // ISO datetime → YYYY-MM-DD
  const [f, setF] = useState({
    name: existing?.name || "",
    typeId: existing?.["configuration-type-id"] || "",
    statusId: existing?.["configuration-status-id"] || "",
    warrantyExpiresAt: d(existing?.["warranty-expires-at"]),
    notes: existing?.notes || "",
    purchasedAt: d(existing?.["purchased-at"]),
    purchasedBy: existing?.["purchased-by"] || "",
    serial: existing?.["serial-number"] || "",
    assetTag: existing?.["asset-tag"] || "",
    manufacturerId: existing?.["manufacturer-id"] || "",
    modelId: existing?.["model-id"] || "",
    locationId: existing?.["location-id"] || "",
    contactId: existing?.["contact-id"] || "",
    installedAt: d(existing?.["installed-at"]),
    installedBy: existing?.["installed-by"] || "",
    position: existing?.position || "",
    hostname: existing?.hostname || "",
    primaryIp: existing?.["primary-ip"] || "",
    macAddress: existing?.["mac-address"] || "",
    defaultGateway: existing?.["default-gateway"] || "",
    platformId: "",                               // not stored on the config; UI filter for OS
    osId: existing?.["operating-system-id"] || "", // retained on edit unless changed
    osNotes: existing?.["operating-system-notes"] || "",
  });
  const [saving, setSaving] = useState(false);
  const set = (patch) => setF(prev => ({ ...prev, ...patch }));

  // Reference data: global manufacturers/platforms + per-org locations/contacts.
  const [manufacturers, setManufacturers] = useState([]);
  const [platforms, setPlatforms] = useState([]);
  const [models, setModels] = useState([]);
  const [oses, setOses] = useState([]);
  const [locations, setLocations] = useState([]);
  const [contacts, setContacts] = useState([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const [extra, locs, cts] = await Promise.all([
        itglue.configExtraRef().catch(() => ({})),
        itglue.orgLocations(orgId).catch(() => []),
        itglue.orgContacts(orgId).catch(() => []),
      ]);
      if (cancelled) return;
      setManufacturers(extra.manufacturers || []);
      setPlatforms(extra.platforms || []);
      setLocations(locs || []);
      setContacts(cts || []);
    })();
    return () => { cancelled = true; };
  }, [itglue, orgId]);

  // Models depend on the chosen manufacturer (lazy). On edit, the existing
  // manufacturer's models load so the saved model shows.
  useEffect(() => {
    if (!f.manufacturerId) { setModels([]); return; }
    let cancelled = false;
    itglue.models(f.manufacturerId).then(m => { if (!cancelled) setModels(m || []); }).catch(() => { if (!cancelled) setModels([]); });
    return () => { cancelled = true; };
  }, [f.manufacturerId, itglue]);

  // Operating systems depend on the chosen platform (lazy).
  useEffect(() => {
    if (!f.platformId) { setOses([]); return; }
    let cancelled = false;
    itglue.operatingSystems(f.platformId).then(o => { if (!cancelled) setOses(o || []); }).catch(() => { if (!cancelled) setOses([]); });
    return () => { cancelled = true; };
  }, [f.platformId, itglue]);

  const num = (v) => (v ? Number(v) : undefined);
  const txt = (v) => (v && v.trim() ? v.trim() : undefined);
  const canSave = f.name.trim() && f.typeId && f.statusId && f.manufacturerId && !saving;

  const save = async () => {
    if (!canSave) { showToast("Name, Type, Status and Manufacturer are required", "err"); return; }
    setSaving(true);
    const attrs = {
      name: f.name.trim(),
      "configuration-type-id": num(f.typeId),
      "configuration-status-id": num(f.statusId),
      "warranty-expires-at": f.warrantyExpiresAt || undefined,
      notes: txt(f.notes),
      "purchased-at": f.purchasedAt || undefined,
      "purchased-by": txt(f.purchasedBy),
      "serial-number": txt(f.serial),
      "asset-tag": txt(f.assetTag),
      "manufacturer-id": num(f.manufacturerId),
      "model-id": num(f.modelId),
      "location-id": num(f.locationId),
      "contact-id": num(f.contactId),
      "installed-at": f.installedAt || undefined,
      "installed-by": txt(f.installedBy),
      position: txt(f.position),
      hostname: txt(f.hostname),
      "primary-ip": txt(f.primaryIp),
      "mac-address": txt(f.macAddress),
      "default-gateway": txt(f.defaultGateway),
      "operating-system-id": num(f.osId),
      "operating-system-notes": txt(f.osNotes),
    };
    try {
      if (isNew) await itglue.createConfig(orgId, attrs);
      else await itglue.updateConfig(existing.id, attrs);
      showToast(isNew ? "Configuration created" : "Configuration updated", "ok");
      onSaved();
    } catch (e) { showToast("Save failed: " + errText(e), "err"); setSaving(false); }
  };

  return (
    <div className="itg-screen">
      <div className="mob-top">
        <button className="back" onClick={onClose}>‹</button>
        <div style={{ flex: 1 }}><h1>{isNew ? "New configuration" : `Edit ${existing?.name || "configuration"}`}</h1></div>
      </div>
      <div className="mob-body">
        <div className="sec-head">Configuration Information</div>
        <div className="f"><label>Name <span className="req">*</span></label>
          <input value={f.name} onChange={e => set({ name: e.target.value })} /></div>
        <div className="f"><label>Type <span className="req">*</span></label>
          <select value={f.typeId} onChange={e => set({ typeId: e.target.value })}>
            <option value="">— Select —</option>
            {(ref0.types || []).map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select></div>
        <div className="f"><label>Status <span className="req">*</span></label>
          <select value={f.statusId} onChange={e => set({ statusId: e.target.value })}>
            <option value="">— Select —</option>
            {(ref0.statuses || []).map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select></div>
        <div className="f"><label>Expiration date</label>
          <input type="date" value={f.warrantyExpiresAt} onChange={e => set({ warrantyExpiresAt: e.target.value })} /></div>
        <div className="f"><label>Notes</label>
          <textarea value={f.notes} onChange={e => set({ notes: e.target.value })} /></div>

        <div className="sec-head">Purchase Information</div>
        <div className="f"><label>Purchase date</label>
          <input type="date" value={f.purchasedAt} onChange={e => set({ purchasedAt: e.target.value })} /></div>
        <div className="f"><label>Purchased by</label>
          <input value={f.purchasedBy} onChange={e => set({ purchasedBy: e.target.value })} /></div>
        <div className="f"><label>Serial number</label>
          <input value={f.serial} onChange={e => set({ serial: e.target.value })} autoCapitalize="none" /></div>
        <div className="f"><label>Asset tag</label>
          <input value={f.assetTag} onChange={e => set({ assetTag: e.target.value })} autoCapitalize="none" /></div>
        <div className="f"><label>Manufacturer <span className="req">*</span></label>
          <select value={f.manufacturerId} onChange={e => set({ manufacturerId: e.target.value, modelId: "" })}>
            <option value="">— Select —</option>
            {manufacturers.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
          </select></div>
        <div className="f"><label>Model</label>
          <select value={f.modelId} onChange={e => set({ modelId: e.target.value })} disabled={!f.manufacturerId}>
            <option value="">{f.manufacturerId ? (models.length ? "— Select —" : "— No models —") : "— Manufacturer first —"}</option>
            {models.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
          </select></div>

        <div className="sec-head">Location Information</div>
        <div className="f"><label>Location</label>
          <select value={f.locationId} onChange={e => set({ locationId: e.target.value })}>
            <option value="">— None —</option>
            {locations.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
          </select></div>
        <div className="f"><label>Contact</label>
          <select value={f.contactId} onChange={e => set({ contactId: e.target.value })}>
            <option value="">— None —</option>
            {contacts.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select></div>
        <div className="f"><label>Install date</label>
          <input type="date" value={f.installedAt} onChange={e => set({ installedAt: e.target.value })} /></div>
        <div className="f"><label>Installed by</label>
          <input value={f.installedBy} onChange={e => set({ installedBy: e.target.value })} /></div>
        <div className="f"><label>Physical position</label>
          <input value={f.position} onChange={e => set({ position: e.target.value })} /></div>

        <div className="sec-head">Device Information</div>
        <div className="f"><label>Hostname</label>
          <input value={f.hostname} onChange={e => set({ hostname: e.target.value })} autoCapitalize="none" /></div>
        <div className="f"><label>Primary IP</label>
          <input value={f.primaryIp} onChange={e => set({ primaryIp: e.target.value })} autoCapitalize="none" /></div>
        <div className="f"><label>Primary MAC</label>
          <input value={f.macAddress} onChange={e => set({ macAddress: e.target.value })} autoCapitalize="none" /></div>
        <div className="f"><label>Default gateway</label>
          <input value={f.defaultGateway} onChange={e => set({ defaultGateway: e.target.value })} autoCapitalize="none" /></div>
        <div className="f"><label>Platform</label>
          <select value={f.platformId} onChange={e => set({ platformId: e.target.value, osId: "" })}>
            <option value="">— Select —</option>
            {platforms.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select></div>
        <div className="f"><label>Operating system</label>
          <select value={f.osId} onChange={e => set({ osId: e.target.value })} disabled={!f.platformId}>
            <option value="">{f.platformId ? (oses.length ? "— Select —" : "— No OS —") : "— Platform first —"}</option>
            {oses.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
          </select></div>
        {!isNew && f.osId && !f.platformId && (
          <div className="hint">Current operating system is kept unless you pick a platform and choose a new one.</div>
        )}
        <div className="f"><label>Operating system notes</label>
          <textarea value={f.osNotes} onChange={e => set({ osNotes: e.target.value })} /></div>
      </div>
      <div className="mob-foot" style={{ display: "flex", gap: 10 }}>
        <button className="btn-secondary" onClick={onClose}>Cancel</button>
        <button className="btn-primary" style={{ flex: 1 }} onClick={save} disabled={!canSave}>{saving ? "Saving…" : isNew ? "Add configuration" : "Save changes"}</button>
      </div>
    </div>
  );
}
